import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, Validators, FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { ApiService } from '../service/api.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../loader/loader.service';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { DomainManagerService } from '../service/domain-manager.service';
import {
  PageChangeEvent,
  GridComponent,
} from '@progress/kendo-angular-grid';
import {
  orderBy,
  State,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { whitespace } from '../common/whitespace';
import { ALL_ROWS_PAGE_SIZE, groupCurrentPage, groupSort, isGrouped, withoutGroups } from '../common/grid-grouping';
import { DiscountType, discountedPrice, discountError, discountPercentage } from '../common/plan-discount';
import { dateCell, ExcelSheetColumn, fileDate, saveExcelSheet, yesNoCell } from '../common/excel-sheet';
import { Observable, Subject, firstValueFrom, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { EditorComponent } from '@progress/kendo-angular-editor';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { APP_DATE_FORMATS } from '../service/date.adapter';
import { EncryptionService } from '../service/encryption.service';
import Validation from '../setting/conform-pass.validator';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MapService } from '../service/map.service';
import { AuthService } from '../service/auth.service';
import { PanelBarExpandMode } from '@progress/kendo-angular-layout';
import { environment } from 'src/environments/environment';
import { SubscriptionPlanType } from '../common/enums/subscription-plan.enum';
import {
  pencilIcon,
  uploadIcon,
  plusCircleIcon,
  arrowRotateCwIcon,
  whatsappIcon,
  sparklesIcon,
  passwordIcon,
  slidersIcon,
  dollarIcon,
  listUnorderedIcon,
  xCircleIcon,
  clockIcon,
  trashIcon,
} from '@progress/kendo-svg-icons';


interface AddOnDetail {
  Id: number;
  AddOnDate: string;
  AddOnCount: number;
  TypeName?: string;
  ExpiryDate?: string;
  AddOnTypes?: {
    TypeName: string;
  };
  addonUsage?: number | string;
}

interface PlanTransaction {
  Id: number;
  TransactionTypeId: number;
  TransactionExpiryDate: string;
  Date: string;
  PlanName: string;
  SubscriptionPlanId: number;
  TransactionStatus: boolean;
  daysRemaining?: number;
  addOns?: AddOnDetail[];
  whatsappStatus?: 'Active' | 'Expired' | null;
  whatsAppExpiryDate: string;
  UsageOverview?: {
    TotalAddonUsers: number;
    TotalPatients: number;
    TotalLocations: number;
    TotalStorage: number;
  }
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  providers: [
    DatePipe,
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
  ],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  readonly icons = {
    edit: pencilIcon,
    migration: uploadIcon,
    addOns: plusCircleIcon,
    changeSubscription: arrowRotateCwIcon,
    whatsapp: whatsappIcon,
    aiAssistant: sparklesIcon,
    resetPassword: passwordIcon,
    planLimits: slidersIcon,
    creditLimit: dollarIcon,
    creditRule: listUnorderedIcon,
    cancelAutoRenew: xCircleIcon,
    planHistory: clockIcon,
    delete: trashIcon,
  };

  /** Matches the backend's EmailTemplate.ReleaseNote description. */
  readonly RELEASE_NOTE_TEMPLATE_NAME = 'Release Note';
  kendoLoading = false;
  public gridData: any;
  keyWordList: any = [
    { text: "KeyWord Replace with Subject Name", id: "[SUBJECT_NAME]" },
    { text: "KeyWord Replace with Client Name", id: "[CLIENT_NAME]" },
    { text: "KeyWord Replace with Domain Name", id: "[DOMAIN_NAME]" },
    { text: "KeyWord Replace with Support Name", id: "[SUPPORT_NAME]" },
    { text: "KeyWord Replace with Contact Number", id: "[CONTACT_NUMBER]" },
    {
      text: "KeyWord Replace with Regards Name",
      id: "[REGARDS_NAME]",
    },
  ];
  @ViewChild("grid", { static: true }) gridKendo: GridComponent;
  @ViewChild(EditorComponent, { static: false })
  kendoEditor: any = EditorComponent;
  @ViewChild('closeModal') closeModal: any;
  @ViewChild('mailCountModal') mailCountModal: any;
  @ViewChild("changePLanConfirmationDialog", { static: true }) changePLanConfirmationDialog!: TemplateRef<any>;
  @ViewChild("changeResetPasswordDialog", { static: true }) changeResetPasswordDialog!: TemplateRef<any>;
  @ViewChild("planLimitDialog") planLimitDialog: TemplateRef<any>;
  @ViewChild("creditLimitDialog") creditLimitDialog: TemplateRef<any>;
  @ViewChild("creditRuleDialog") creditRuleDialog: TemplateRef<any>;
  public kendoGridLoading = false;
  public kendoData: any;
  state: State = {
    skip: 0,
    take: 10,
    filter: {
      logic: 'and',
      filters: [],
    },
    group: [],
  };
  public pageableWithAll: any = {
    buttonCount: 5,
    pageSizes: [5, 10, 20, 50, 100, { text: 'All', value: 'all' }]
  };
  public sort: SortDescriptor[] = [];
  // Some grid columns can't be sorted correctly by asking the backend to order the underlying
  // `IFOUR_CompanyMaster` query (`ToDataSourceResultAsync` in GetPlusPhysioDomain/GetPlusDoctorDomain):
  //  - "Migration Version Info" is a real column, but it's only populated for the rows an admin
  //    explicitly migrated from this panel - it's null/stale for most companies, so sorting the
  //    raw column is a no-op for most rows.
  //  - "Subscription Plan" (SubscriptionPlanName) and "Status" (Status) aren't columns on the
  //    entity at all (see IFOUR_CompanyMaster.cs - it has no Status property, only
  //    MigrationStatus) - both are derived and only set on the DTO *after* the backend's
  //    ToDataSourceResultAsync call has already paged/sorted (SetSubscriptionDetails /
  //    SetCompanyStatus in IfourCompanyMasterService.cs), so asking the backend to sort by either
  //    field name fails - the field doesn't exist on the query, so it comes back with no rows.
  // CLIENT_SORT_COMPARATORS below drives a client-side full-list sort for all of these, keyed off
  // the value actually rendered in that column - see applySortOrLoad()/loadFullList().
  private readonly CLIENT_SORT_COMPARATORS: Record<string, (a: any, b: any) => number> = {
    MigrationVersionInfo: (a, b) =>
      (this.isNewPlatformMigrated(a) ? 1 : 0) - (this.isNewPlatformMigrated(b) ? 1 : 0),
    SubscriptionPlanName: (a, b) =>
      (a.SubscriptionPlanName ?? '').localeCompare(b.SubscriptionPlanName ?? ''),
    Status: (a, b) => (a.Status ?? '').localeCompare(b.Status ?? ''),
  };
  // Full-list mode: every row matching the current search and filters is fetched once and paged
  // locally. Used while one of the columns above is sorted, and while the grid is grouped so that
  // each group spans the whole list rather than a single page - see applySortOrLoad().
  private fullListActive = false;
  // The backend sort and column filters the loaded rows were fetched with - different ones need a
  // new fetch
  private fullListKey: string | undefined;
  private fullListRows: any[] = [];
  // Guards against firing a second full-list fetch when Kendo emits both sortChange and
  // dataStateChange for one header click (see applySortOrLoad()), and against a slow fetch's
  // response landing after a newer sort/search/clear has already moved on (see
  // loadFullList()/resetFullList()).
  private fullListLoading = false;
  /** An Excel export is fetching its rows - see exportClients(). */
  exportingClients = false;
  private fullListRequestId = 0;
  // The grid state the rows on screen were last requested for (see describeGridState()). Kendo's
  // filter menu edits the bound filter object - this.state.filter - in place before it emits the
  // change, so comparing an incoming state with this.state would always find the new filter already
  // applied and skip the fetch; compare with this snapshot instead.
  private appliedStateKey = '';
  searchString: any = '';
  filterArray: any = [];
  filetrColumn: any = [];
  textboxValue: any = [];
  datePickerValue: any = [];
  selectedRecord: any;
  formValue!: FormGroup;
  updateItem: any;
  allAdminData: any = [];
  showAdd!: boolean;
  showbtn!: boolean;
  emailerror: boolean = false
  isDisabled: boolean = false;
  isDisableUpdate: boolean = false;
  onEditChangeStatus: boolean = false;
  hideTemplateId: any
  p: number = 1;
  total: number = 0;
  remainingCreditLimit: number = 0;
  toppingList = [
    'Extra cheese',
    'Mushroom',
    'Onion',
    'Pepperoni',
    'Sausage',
    'Tomato',
  ];
  currencyList: any = [];
  dropdownValues = [
    { text: 'Registered', value: 'Registered' },
    { text: 'OTP Verified', value: 'OTPVerified' },
    { text: 'Logged In', value: 'LoggedIn' },
  ];
  public templateBody: string = '';
  public Body: string = '';
  matDialogRef!: MatDialogRef<any>;
  confirmationDialog!: MatDialogRef<any>;
  deletedData: any;
  domain: any;
  IsDisable: boolean = false;
  DateFormat: string | undefined;
  selectedPlan: string = 'All';
  selectedAccountType: string = 'Live';
  selectedStatus: string = 'All';
  planOptions = [
    { label: 'All', value: 'All' },
    { label: 'Basic Plan', value: '1' },
    { label: 'Standard Plan', value: '2' },
    { label: 'Premium Plan', value: '3' },
    { label: 'Enterprise Plan', value: '4' },
  ];
  accountTypeOptions = [
    { label: 'All', value: 'All' },
    { label: 'Test', value: 'Test' },
    { label: 'Live', value: 'Live' },
  ];
  statusFilterOptions = [
    { label: 'All', value: 'All' },
    { label: 'Registered', value: 'Registered' },
    { label: 'OTP Verified', value: 'OTPVerified' },
    { label: 'Logged In', value: 'LoggedIn' },
  ];
  companyId = 0;
  status: any;
  selectedCompanyId: any;
  selectedData: any = [];
  createMailForm!: FormGroup;
  addMailForm!: FormGroup;
  clientData: any[] = [];
  public isChecked = false;
  public value: any = [];
  public subject: string = '';
  public Name: string = '';
  TotalEmails: any;
  TotalSendEmails: any;
  TotalUnSendEmails: any;
  CompantData: any = [];
  public isShow = false;
  isInputFieldEmpty: boolean = true;
  isTemplateInputFieldEmpty: boolean = true;
  templateData: any;
  TemplateId: any;
  EmailData: any;
  Id: any;
  templateName: any;
  IsTemplateColor = true;
  IsTemplateClose: boolean = true;
  IsDoctor: boolean = false;
  IsTest: boolean = false;
  screenWidth: number;
  ClientColumnWidth: number = 150;
  TableName = "Client Details";
  SubscriptionPlanType = SubscriptionPlanType;
  displayColumns: any;
  auto: number;
  gridHeight: number;
  screenHeight: number;
  addOns: { id: number, name: string; quantity: number; price: number; previousQuantity: number; }[] = [];
  originalAddOns = [
    { id: 1, name: 'User', price: 500, previousQuantity: 0 },
    { id: 2, name: 'Location', price: 500, previousQuantity: 0 },
    { id: 3, name: 'Patients', price: 500, previousQuantity: 0 },
    { id: 4, name: 'Storage', price: 500, previousQuantity: 0 }
  ];
  plans: {
    id: number,
    name: string;
    monthlyPrice: string;
    annualPrice: string;
    prices?: { [key: string]: { monthly: string; annual: string } };
    isShowAddOn: boolean,
    expriyDate: any,
    // Discount taken off the price for clients billed offline - see common/plan-discount.ts
    discountValue?: number | null,
    discountType?: DiscountType
  }[] = [];
  selectedPlanId: number = 0;
  isPlanYearly: boolean = false;
  planPrices: any = {
    [SubscriptionPlanType.BasicPlan]: { INR: { monthly: '0', annual: '0' }, USD: { monthly: '0', annual: '0' } },
    [SubscriptionPlanType.StandardPlan]: { INR: { monthly: '999', annual: '829' }, USD: { monthly: '12', annual: '10' } },
    [SubscriptionPlanType.PremiumPlan]: { INR: { monthly: '2499', annual: '2075' }, USD: { monthly: '28', annual: '23' } },
    [SubscriptionPlanType.EnterprisePlan]: { INR: { monthly: '-', annual: '-' }, USD: { monthly: '-', annual: '-' } }
  };
  newPlan: any;
  ipAddress: any;
  whatsappForm!: FormGroup;
  locationList: { Id: number; Name: string }[] = [];
  isWhatsappSubmitted: boolean = false;
  today = new Date();
  expiryDate: any;
  selectedTemplate: any;
  hideConfirmPassword: boolean = true;
  hideNewPassword: boolean = true;
  form!: FormGroup;
  loading: boolean = false;
  selectedUser: any;
  planLimitForm!: FormGroup;
  creditLimitForm!: FormGroup;
  IsReleaseNote: boolean = false;
  isMaximized = false;
  originalOverlayStyle: Partial<CSSStyleDeclaration> = {};
  originalDialogStyle: Partial<CSSStyleDeclaration> = {};
  isMaximizedEmail = false;
  originalOverlayStyleEmail: Partial<CSSStyleDeclaration> = {};
  originalDialogStyleEmail: Partial<CSSStyleDeclaration> = {};
  previousPlan: PlanTransaction[] = [];
  currentPlan: PlanTransaction | null = null;
  activeTab: number = 0;
  PanelBarExpandMode = PanelBarExpandMode
  selectedCurrencyId: number;
  currencyMap: any = [];
  isSendInvoice: boolean = true;
  isRadiologyDomain: boolean = false;
  private readonly destroy$ = new Subject<void>();
  isAIEnabled: boolean = false;
  usageOverviewData: any[] = [];
  private readonly aiKeyName: string = 'AIChatEnable';
  creditRuleForm!: FormGroup;

  constructor(
    private readonly FormBuilder: FormBuilder,
    private readonly api: ApiService,
    private readonly toaster: ToastrService,
    private readonly LoaderService: LoaderService,
    private readonly dialog: MatDialog,
    public datePipe: DatePipe,
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef,
    private readonly encryptionService: EncryptionService,
    private readonly overlayContainer: OverlayContainer,
    private readonly mapService: MapService,
    private readonly authService: AuthService,
    private readonly domainService: DomainManagerService
  ) {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
  }

  ngOnInit(): void {
    const currentUrl = this.router.url.toLowerCase();

    if (this.authService.isAuthenticated()) {
      if (!currentUrl.includes('user') && !currentUrl.includes('client')) {
        this.router.navigate(['/dashboard']);
      }
    }
    this.formValue = this.FormBuilder.group({
      FirstName: ['', [Validators.required, whitespace]],
      CompanyName: [''],
      Salutation: [''],
      Email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$'),
          whitespace,
        ],
      ],
      PhoneNumber: ['', [Validators.required, whitespace]],
      CompanyAddress: ['', [Validators.required, whitespace]],
      CurrencyId: ['', Validators.required],
      IsActive: [true],
      IsTestAccount: [false]
    });

    this.formInIt();
    const container = this.overlayContainer.getContainerElement();
    container.style.zIndex = '2000';
    this.emailInit()
    this.getCountry()
    this.whatsappFormInit();
    this.resetInit();
    this.IsTest = this.domainService.isTestDomain;
    this.isRadiologyDomain = this.domainService.isRadiologyDomain;
    this.getDomain();
    this.setGridHeight();

    setTimeout(() => {
      if (!localStorage.getItem("IA")) {
        this.api.getIPAddress().subscribe((x: any) => {
          const ipAddress = x.ip;
          this.ipAddress = ipAddress;
          localStorage.setItem("IA", this.ipAddress);
        });
      } else {
        this.ipAddress = localStorage.getItem("IA");
      }
    }, 200);
    this.getAllNewTemplate();
    this.planLimitInit();
    this.creditLimitInit();
    this.getCurrency();
    this.creditRuleInit();
  }
  public get isIndet() {
    return (
      this.value.length !== 0 && this.value.length !== this.clientData.length
    );
  }
  private loadData(): void {
    // allAdminData is always the current page's flat rows (the usage results below are matched to
    // them by index); when grouped they already come in group order from applyFullListPage()
    this.gridData = groupCurrentPage(this.allAdminData, this.total, this.state);
  }
  public dataStateChange(state: any): void {
    // Kendo's grid emits its own dataStateChange as an echo of whatever we already handled via
    // sortChange()/pageChange() (both are bound separately below), and again right after [data]
    // is first (re)assigned on load - in every one of those cases skip/take/sort/filter/group here
    // are already identical to what's already applied, so re-running applySortOrLoad() would just
    // fire a second, redundant GetPlusPhysioDomain/GetPlusDoctorDomain call for the same page.
    // Only fetch when this event actually carries a real change.
    const isSameState = this.describeGridState(state) === this.appliedStateKey;
    this.state = state;
    this.sort = state.sort ?? [];
    if (isSameState) return;
    this.applySortOrLoad();
  }

  private describeGridState(state: any): string {
    return JSON.stringify({
      skip: state?.skip,
      take: state?.take,
      sort: state?.sort ?? [],
      filter: state?.filter ?? null,
      group: state?.group ?? [],
    });
  }

  private markStateApplied(): void {
    this.appliedStateKey = this.describeGridState(this.state);
  }
  public sortChange(sort: SortDescriptor[]): void {
    // Kendo fires sortChange AND dataStateChange for the same header click when both are bound
    // (they are - see the grid template) - whichever handler runs second would otherwise see the
    // new sort already applied by the first and fire a redundant fetch. Skip when this call is a
    // no-op given what's already been loaded.
    this.sort = sort;
    this.state.sort = sort;
    if (this.describeGridState(this.state) === this.appliedStateKey) return;
    this.applySortOrLoad();
  }
  public pageChange(event: PageChangeEvent): void {
    // Same double-fire risk as sortChange() above, for pageChange + dataStateChange.
    this.state.skip = event.skip;
    this.state.take = event.take;
    if (this.describeGridState(this.state) === this.appliedStateKey) return;
    if (this.fullListActive) {
      this.markStateApplied();
      this.applyFullListPage();
      return;
    }
    this.fetchDomainPage();
  }

  private findClientSort(): SortDescriptor | undefined {
    return this.sort.find(s => !!s.dir && !!this.CLIENT_SORT_COMPARATORS[s.field]);
  }

  private applySortOrLoad(): void {
    this.markStateApplied();
    const clientSort = this.findClientSort();
    if (!clientSort && !isGrouped(this.state)) {
      this.resetFullList();
      this.fetchDomainPage();
      return;
    }

    // A column only the client can sort is fetched in the backend's own order and sorted in
    // applyFullListPage(); any other sort is still applied by the backend
    const serverSort = clientSort ? [] : (this.state.sort ?? []);
    const key = JSON.stringify({ sort: serverSort, filter: this.state.filter ?? null });
    this.fullListActive = true;

    if (key === this.fullListKey && this.fullListRows.length) {
      // The loaded rows still apply (a regroup, a client-only sort flip, or the grid's own
      // sortChange + dataStateChange both firing for one click) - re-page them, no API call.
      this.applyFullListPage();
      return;
    }

    if (key === this.fullListKey && this.fullListLoading) {
      // The same fetch is still in flight - its callback will page/render once it resolves;
      // don't fire a second identical call.
      return;
    }

    this.fullListKey = key;
    this.loadFullList(serverSort);
  }

  // Fetches every row matching the current search and filters (not just the current page), then
  // pages through them locally. No backend change - this reuses the same paginated endpoint with a
  // page size large enough to return everything. Fires exactly once per distinct backend sort and
  // filter (see the in-flight/stale-response guards in applySortOrLoad() and the requestId check
  // below).
  private loadFullList(serverSort: SortDescriptor[]): void {
    this.LoaderService.show();
    this.fullListLoading = true;
    const requestId = ++this.fullListRequestId;
    const fetchAllState: State = {
      ...this.state,
      skip: 0,
      take: ALL_ROWS_PAGE_SIZE,
      sort: serverSort,
      group: [],
    };
    const observable = environment.environmentType === 'plusdoctor'
      ? this.api.GetPlusDoctorDomain(fetchAllState, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus)
      : this.api.GetPlusPhysioDomain(fetchAllState, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus);

    observable.subscribe({
      next: (res: any) => {
        this.fullListLoading = false;
        this.LoaderService.hide();
        if (requestId !== this.fullListRequestId) {
          // A newer sort, search, or clear happened while this fetch was in flight - its result
          // is stale, discard it instead of clobbering whatever is now on screen.
          return;
        }
        this.fullListRows = res?.Data ? res.Data.map((e: any) => this.transformCompany(e)) : [];
        this.applyFullListPage();
      },
      error: (err: any) => {
        this.fullListLoading = false;
        this.LoaderService.hide();
        if (requestId !== this.fullListRequestId) return;
        console.error('Error fetching all domain data:', err);
        this.toaster.error('Something went wrong while fetching record');
      }
    });
  }

  /**
   * Saves every client matching the current search, filters and sort - not just this page - as
   * Clients_22-10-2026.xlsx, with the grid's columns. Add-ons list what was bought: how much of each is used
   * needs a usage call per client, which the grid only makes for the page it shows.
   */
  exportClients(): void {
    const clientSort = this.findClientSort();
    const exportState: State = {
      ...this.state,
      skip: 0,
      take: ALL_ROWS_PAGE_SIZE,
      sort: clientSort ? [] : (this.state.sort ?? []),
      group: [],
    };
    const observable = environment.environmentType === 'plusdoctor'
      ? this.api.GetPlusDoctorDomain(exportState, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus)
      : this.api.GetPlusPhysioDomain(exportState, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus);

    this.exportingClients = true;
    this.LoaderService.show();
    observable.subscribe({
      next: (res: any) => {
        this.exportingClients = false;
        this.LoaderService.hide();
        // transformCompany() turns CreatedDate into display text - keep the real date for the sheet
        let rows: any[] = (res?.Data ?? []).map((e: any) => ({ ...this.transformCompany({ ...e }), CreatedOn: e.CreatedDate }));
        if (clientSort) {
          const comparator = this.CLIENT_SORT_COMPARATORS[clientSort.field];
          rows = rows.sort((a, b) => clientSort.dir === 'asc' ? comparator(a, b) : comparator(b, a));
        }
        if (!rows.length) {
          this.toaster.info('No data to export');
          return;
        }
        saveExcelSheet(`Clients_${fileDate()}.xlsx`, this.clientExportColumns(), rows);
      },
      error: () => {
        this.exportingClients = false;
        this.LoaderService.hide();
        this.toaster.error('Something went wrong while exporting clients');
      }
    });
  }

  /** The client grid's columns (all but the row menu), for its Excel export. */
  private clientExportColumns(): ExcelSheetColumn<any>[] {
    const addOnText = (d: any) => {
      const expiry = d.ExpiryDate ? ` - ${this.datePipe.transform(d.ExpiryDate, 'dd/MM/yyyy')}` : '';
      return `${d.AddOnCount} - ${d.TypeName} (${this.datePipe.transform(d.AddOnDate, 'dd/MM/yyyy')}${expiry})`;
    };
    return [
      { title: 'Doctor Name', width: 180, cell: r => ({ value: `${r.Salutation ?? ''} ${r.FirstName} ${r.LastName ?? ''}`.trim() }) },
      { title: 'Clinic Name', width: 200, cell: r => ({ value: r.CompanyName }) },
      { title: 'Phone Number', width: 120, cell: r => ({ value: r.PhoneNumber }) },
      { title: 'Appointment Count', width: 130, cell: r => ({ value: r.AppointmentCount }) },
      { title: 'Patient Count', width: 110, cell: r => ({ value: r.PatientCount }) },
      { title: 'Total Users', width: 100, cell: r => ({ value: `${r.ActiveUser ?? 0} (${r.InActiveUser ?? 0})` }) },
      { title: 'Domain', width: 260, cell: r => ({ value: r.Domain }) },
      { title: 'Email', width: 240, cell: r => ({ value: r.Email }) },
      { title: 'Verification Code', width: 120, cell: r => ({ value: r.VerificationCode }) },
      { title: 'Status', width: 120, cell: r => ({ value: r.Status ? this.getStatusText(r.Status) : '' }) },
      { title: 'Active', width: 70, cell: r => yesNoCell(r.IsActive) },
      { title: 'Email-Verify', width: 90, cell: r => yesNoCell(r.IsEmailVerified) },
      { title: 'Address', width: 260, cell: r => ({ value: r.CompanyAddress, wrap: true }) },
      { title: 'Test Account', width: 100, cell: r => yesNoCell(r.IsTestAccount) },
      { title: 'Migration Status', width: 130, cell: r => ({ value: r.MigrationStatus }) },
      { title: 'Created Date', width: 100, cell: r => dateCell(r.CreatedOn, '', 'dd-mm-yyyy') },
      { title: 'IP Address', width: 140, cell: r => ({ value: r.IPAddress }) },
      { title: 'IP Location', width: 140, cell: r => ({ value: r.IPLocation }) },
      { title: 'WhatsApp Integration', width: 140, cell: r => ({ value: r.IsWhatsAppIntegration }) },
      { title: 'Country', width: 110, cell: r => ({ value: r.Country }) },
      { title: 'Subscription Plan', width: 140, cell: r => ({ value: r.SubscriptionPlanName }) },
      { title: 'Autopay', width: 80, cell: r => ({ value: r.IsAutoRenew ? 'Enabled' : 'Disabled' }) },
      { title: 'Payment Link', width: 260, cell: r => ({ value: r.RzpPaymentLinkUrl }) },
      { title: 'Plan Expiry Date', width: 110, cell: r => dateCell(r.PlanExpiryDate, '', 'dd/mm/yyyy') },
      { title: 'Add-On', width: 300, cell: r => ({ value: (r.AddOnDetails ?? []).map(addOnText).join('\n'), wrap: true }) },
    ];
  }

  private applyFullListPage(): void {
    let rows = this.fullListRows;
    const clientSort = this.findClientSort();
    if (clientSort) {
      const comparator = this.CLIENT_SORT_COMPARATORS[clientSort.field];
      rows = [...rows].sort((a: any, b: any) => clientSort.dir === 'asc' ? comparator(a, b) : comparator(b, a));
    }
    if (isGrouped(this.state)) {
      // Each group's rows sit together, keeping the order above within each group
      rows = orderBy(rows, groupSort(this.state.group));
    }

    const skip = this.state.skip ?? 0;
    const take = this.state.take ?? 10;
    this.allAdminData = rows.slice(skip, skip + take);
    this.total = rows.length;

    if (this.allAdminData.length > 0) {
      forkJoin(
        this.allAdminData.map((company: any) =>
          forkJoin({
            usage: this.api.getUsageOverview(company.Id),
            addOns: this.api.getAddOnDetails(company.Id)
          })
        )
      ).subscribe((results: any) => this.applyUsageResults(results));
    }

    this.loadData();
  }

  // Drops the loaded full list and discards any fetch still in flight, so its stale response can't
  // land after whatever change made it stale
  private resetFullList(): void {
    this.fullListActive = false;
    this.fullListKey = undefined;
    this.fullListRows = [];
    this.fullListLoading = false;
    this.fullListRequestId++;
  }
  getCountry() {
    this.api.getCountry().subscribe((res) => {
      if (res.Success) {
        this.currencyList = res['Data'];
      }
    })
  }

  public handleBlurEmail(_controlName: any) {
    let oldEmail = this.updateItem.Email;
    let matchEmailValue = this.formValue.value.Email;
    this.api.matchEmailName(matchEmailValue).subscribe((res: any) => {
      if (matchEmailValue === oldEmail) {
        this.emailerror = false;
      } else if (res.ResponseCode === 404) {
        this.emailerror = true;
      } else {
        this.emailerror = false;
      }
    });
  }

  addMigration() {
    this.IsDisable = true;
    this.LoaderService.show();
    this.api.addMigration().subscribe({
      next: (_res) => {
        this.toaster.success('All Migration Run Successfully..');
        this.matDialogRef.close();
        this.LoaderService.hide();
        setTimeout(() => {
          this.IsDisable = false;
        }, 120000);
      },
      error: (_err: any) => {
        this.IsDisable = false;
        this.toaster.error('Something went wrong');
      },
    });
  }

  //Delete Records We are creating method
  DomainUrl: any;
  SubPlanUrl: any;
  DashboardUrlFlag: boolean = false;
  SubPlanUrlFlag: boolean = false;
  AdminLoginData(data: any, domain: any, flag: any) {
    if (flag == "DomainUrl") {
      this.DashboardUrlFlag = true;
      this.SubPlanUrlFlag = false;
    }
    else {
      this.SubPlanUrlFlag = true;
      this.DashboardUrlFlag = false;
    }
    this.api.AdminLoginData(data, domain).subscribe({
      next: (res: any) => {
        console.log(res);

        if (res.Success) {
          localStorage.setItem("clickhere", JSON.stringify(true));
          localStorage.setItem("token", res["AccessToken"]);
          localStorage.setItem("Name", res["Data"].Name);
          localStorage.setItem("userId", res["Data"].Id);
          localStorage.setItem("companyId", res["Data"].CompanyId);
          localStorage.setItem("CompanyName", res["Data"].CompanyName);
          localStorage.setItem("DoctorTypeId", res["Data"].DoctorTypeId);
          localStorage.setItem("Location", res["Data"].LocationId);
          localStorage.setItem("AspNetRole", res["Data"].AspNetRoleList);
          localStorage.setItem("selectedPlan", res["Data"].selectedPlan);
          localStorage.setItem("AdminLogin", 'true');
          const subscriptionAccesses = res.Data.SubscriptionAccesses ?? [];
          const accessObject = subscriptionAccesses.reduce((acc: { [key: string]: boolean }, item: any) => {
            acc[item.key] = item.IsAccess;
            return acc;
          }, {});
          localStorage.setItem("FeatureAccess", JSON.stringify(accessObject));
          this.api.getDefaultLocation(res["Data"].Id).subscribe({
            next: (response: any) => {
              if (response["Data"] != null && response["Success"] === true) {
                localStorage.setItem(
                  "Location",
                  JSON.stringify({
                    Id: response["Data"].LocationId,
                    Name: response["Data"].LocationName,
                  })
                );

                this.getFormat(
                  res["Data"].CompanyId,
                  res["PermissionData"]
                );
              } else {
                this.getFormat(
                  res["Data"].CompanyId,
                  res["PermissionData"]
                );
              }
            }

          });

        }
        else {
          this.toaster.error("Something went wrong in the login process sorry");
        }

      }, error: (err: any) => {
        this.toaster.error("Something went wrong in the login process sorry");
      }
    })
  }

  async getFormat(id: any, PermissionData: any) {
    try {
      const res: any = await firstValueFrom(this.api.getCompanyByIdpromise(id));

      if (res["Success"] !== true) {
        this.toaster.error("Something went wrong in the login process sorry");
        return;
      }

      const data = res["Data"];
      const storeKeys = (keys: Record<string, any>) => {
        Object.keys(keys).forEach(k => localStorage.setItem(k, keys[k]));
      };

      storeKeys({
        "Referral": data["IsShowReferral"],
        "IsVisibleAdvice": data["IsVisibleAdvice"],
        "IsVisibleDiagnosis": data["IsVisibleDiagnosis"],
        "IsVisibleDocument": data["IsVisibleDocument"],
        "IsVisibleInvestigation": data["IsVisibleInvestigation"],
        "IsVisibleInvoice": data["IsVisibleInvoice"],
        "IsVisiblePayment": data["IsVisiblePayment"],
        "IsVisiblePrescription": data["IsVisiblePrescription"],
        "IsVisibleReference": data["IsVisibleReference"],
        "IsVisibleImmunization": data["IsVisibleImmunization"]
      });

      if (data.CurrencyObj?.CurrencySymbol) {
        localStorage.setItem("Currency", data.CurrencyObj.CurrencySymbol);
      }

      const response: any = await firstValueFrom(this.api.getAll(id));

      if (response["Success"] !== true) return;

      const settings = response['Data'];
      const findValue = (key: string) => settings.find((x: any) => x.Key === key)?.Value;

      const Dformat = findValue("CompanyDateFormat");
      const decimalvalue = findValue("decimal");
      const Referralvalue = findValue("Referral");
      const TimeZonevalue = findValue("TimeZone");

      storeKeys({
        "Dformat": Dformat,
        "decimal": decimalvalue,
        "Referral": Referralvalue,
        "TimeZone": TimeZonevalue
      });

      // Prepare routing variables
      const domain = data.Domain;
      const token = localStorage['token'];
      const Id = localStorage['userId'];
      const CompanyId = data.Id;
      const CompanyName = localStorage['CompanyName'];
      const location = localStorage['Location'];
      const name = data.FirstName;
      const docId = data.DoctorTypeId ?? null;
      const Currency = localStorage['Currency'];
      const IP = localStorage['IA'];
      const AspNetRole = localStorage['AspNetRole'];
      const ReferralLS = localStorage['Referral'];
      const selectedPlan = localStorage['selectedPlan'];
      const AdminLogin = localStorage['AdminLogin'];

      // Decide routing page
      const isDashboard = this.DashboardUrlFlag;
      const routingPage = isDashboard ? "Dashboard" : "Subscription Plan";
      localStorage.setItem("routingPage", routingPage);

      const protocol = domain.includes("ifourtechlab.com") ? "http" : "https";
      const encodedRoutingPage = encodeURIComponent(routingPage);

      // Build URL
      const url = isDashboard
        ? `${protocol}://${domain}/#/Dashboard?${encodeURIComponent(token)}&${encodeURIComponent(Id)}&${encodedRoutingPage}`
        : `${protocol}://${domain}/#/Subscription%20Plan?${encodeURIComponent(token)}&${encodeURIComponent(Id)}&${encodeURIComponent(CompanyId)}&${encodeURIComponent(name)}&${encodeURIComponent(docId)}&${encodeURIComponent(Dformat)}&${encodeURIComponent(decimalvalue)}&${encodeURIComponent(location)}&${encodeURIComponent(TimeZonevalue)}&${encodeURIComponent(Currency)}&${encodeURIComponent(IP)}&${encodeURIComponent(AspNetRole)}&${encodeURIComponent(ReferralLS)}&${encodeURIComponent(CompanyName)}&${encodedRoutingPage}&${encodeURIComponent(selectedPlan)}&${encodeURIComponent(AdminLogin)}`;

      localStorage.setItem("clickhere", JSON.stringify(false));
      window.open(url, "_blank");

    } catch (error) {
      console.error("Error in getFormat:", error);
      this.toaster.error("Something went wrong while fetching company data");
    }
  }
  DeleteClientData() {
    var data = this.deletedData.Id;
    this.LoaderService.show();
    this.api.deleteClientData(data).subscribe({
      next: (res: any) => {
        this.LoaderService.hide();
        if (res && res.Success) {
          this.matDialogRef.close();
          this.toaster.success('Record Deleted Successfully..');
          this.getDomain();
          this.loadData();
        } else {
          this.toaster.error(res?.Message || 'Something went wrong Record not deleted sorry');
        }
      },
      error: (_err: any) => {
        this.LoaderService.hide();
        this.toaster.error('Something went wrong Record not deleted sorry');
      },
    });
  }

  RunMigrationData() {
    if (this.companyId != 0) {
      var Data = localStorage.getItem('companyId');
      this.LoaderService.show();
      this.api.runMigrationData(this.companyId).subscribe({
        next: (res: any) => {
          this.LoaderService.hide();
          if (res && res.Success) {
            this.matDialogRef.close();
            this.toaster.success('Migration Run Successfully');
            this.companyId = 0;
            this.getDomain();
            this.loadData();
          } else {
            this.toaster.error(res?.Message || 'Something went wrong, migration not run');
          }
        },
        error: (_err: any) => {
          this.LoaderService.hide();
          this.toaster.error('Something went wrong, migration not run');
        },
      });
    }
  }

  closeEditDialog() {
    this.emailerror = false;
    this.closeModal.nativeElement.click();
  }

  EditClientData(data: any, templateRef: TemplateRef<any>) {
    this.updateItem = data;
    this.showAdd = false;
    this.showbtn = true;
    this.formValue.patchValue(data);
    this.formValue.controls['IsActive'].setValue(data.IsActive.toString());
    this.onEditChangeStatus = data.IsActive;
    this.isDisableUpdate = !this.onEditChangeStatus;
    this.formValue.controls['IsTestAccount'].setValue(data.IsTestAccount.toString());
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      autoFocus: false,
    });
  }

  getValid() {
    return this.formValue.invalid || this.emailerror;
  }

  onStatusChange(isActive: boolean) {
    this.isDisableUpdate = this.onEditChangeStatus ? false : !isActive;
  }
  updateAdmin() {
    if (this.formValue.invalid || this.emailerror) {
      this.formValue.markAllAsTouched();
      return;
    }
    let obj = {
      Id: this.updateItem.Id,
      Salutation: this.formValue.value.Salutation,
      FirstName: this.formValue.value.FirstName?.trimStart(),
      MobileNumber: this.formValue.value.PhoneNumber,
      PhoneNumber: this.formValue.value.PhoneNumber,
      Gender: this.updateItem.Gender,
      Email: this.formValue.value.Email?.trimStart(),
      CompanyCode: this.updateItem.CompanyName,
      CompanyName: this.updateItem.CompanyName,
      CompanyAddress: this.formValue.value.CompanyAddress?.trimStart(),
      Domain: this.updateItem.Domain,
      IsActive: this.formValue.value.IsActive,
      IsTestAccount: this.formValue.value.IsTestAccount,
      ConnectionString: this.updateItem.ConnectionString,
      ConnectionStringEF: this.updateItem.ConnectionStringEF,
      CurrencyId: this.formValue.value.CurrencyId,
      CompanyDateFormat: this.updateItem.CompanyDateFormat,
      CompanyDecimalFormat: this.updateItem.CompanyDecimalFormat,
      Status: this.updateItem.Status,
      PatientCount: this.updateItem.PatientCount,
      AppointmentCount: this.updateItem.AppointmentCount,
      MigrationStatus: this.updateItem.MigrationStatus,
      MigrationVersionInfo: this.updateItem.MigrationVersionInfo,
      IPAddress: this.updateItem.IPAddress,
      IsTermsAndCondition: this.updateItem.IsTermsAndCondition,
      IsDoctor: this.updateItem.IsDoctor,
      CurrencyObj: {
        Id: 0,
        IsDeleted: true,
        CurrencyCode: '',
        CurrencySymbol: '',
      },
    };


    this.api.updateForAdmin(obj).subscribe({
      next: (res: any) => {
        if (res?.Success && res?.Data) {
          this.toaster.success('User Record Updated Successfully.');
          this.formValue.reset();

          this.getDomain();
          this.loadData();
          this.closeDialog();
        }
        else {
          let message = 'User Record Not Updated.';

          switch (res?.ResponseCode) {
            case 403:
              message = 'Email ID mismatch in domain & main database';
              break;
            case 405:
              message = 'Phone Number already exists.';
              break;
            case 436:
              message = 'Company already exists.';
              break;
            case 409:
              message = 'User not found.';
              break;
            case 551:
              message = 'Domain not found.';
              break;
            case 552:
              message = 'User migration failed.';
              break;
            case 553:
              message = 'User database deleted.';
          }

          this.toaster.error(message);
        }
      },
      error: () => {
        this.toaster.error('Something went wrong. Record not updated.');
      }
    });
  }

  pageChangeEvent(event: number) {
    this.p = event;
    this.allAdminData();
  }

  keyPress(event: any) {
    const pattern = /[0-9\+\-\ ]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode != 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  onlyDigits(event: any) {
    const pattern = /[0-9]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode != 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }
  closeDialog() {
    this.dialog.closeAll();
  }
  openDelateDialog(data: any, templateRef: TemplateRef<any>) {
    this.deletedData = data;
    this.domain = data.Domain;
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
  }
  openAllMigrationDialog(_data: any, templateRef: TemplateRef<any>) {
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
  }

  openMigrationDialog(data: any, templateRef: TemplateRef<any>) {
    if (data?.Id != 0) {
      this.companyId = data.Id;
    }
    this.domain = data.Domain;
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
  }
  //Search bar

  filterData(event: any) {
    this.searchString = event;
    this.state.skip = 0;
    // A new search invalidates the loaded full list (it was fetched for the previous search text)
    // and drops back to normal per-page sorting until the user re-sorts; grouping stays, and
    // reloads the full list for the new search.
    // The API matches the search text itself (SearchText); the column filters stay as they are.
    this.resetFullList();
    this.sort = [];
    this.state.sort = [];
    this.loadData();
    this.applySortOrLoad();
  }
  selectedOption(event: any, dataItem: any) {
    this.status = event.value;
    this.selectedCompanyId = dataItem.Id;
    this.LoaderService.show();
    this.api.updateStatus(this.selectedCompanyId, this.status).subscribe(
      (res: any) => {
        this.LoaderService.hide();
        if (res['Success'] === true) {
          if (res['ResponseCode'] === 200) {
            dataItem.Status = event.value;
            this.toaster.success('Status Updated Successfully.');
          }
        } else {
          this.getDomain();
          this.toaster.error('Status update failed.');
        }
      },
      (_err) => {
        this.LoaderService.hide();
        this.toaster.error('Something went wrong Record not Updated sorry');
      }
    );
  }
  checkboxChecked(event: any) {
    let selected: any[] = event.selectedRows;
    let deselected: any[] = event.deselectedRows;
    if (selected.length > 0) {
      selected.map((itm) => {
        this.selectedData.push(itm.dataItem);
      });
    }

    if (deselected.length > 0) {
      deselected.map((itm) => {
        let ind = this.selectedData.findIndex(
          (x: { Id: any }) => x.Id == itm.dataItem.Id
        );
        if (ind != -1) {
          this.selectedData.splice(ind, 1);
        }
      });
    }
  }
  async add(templateRef: TemplateRef<any>) {
    this.isMaximized = false;
    this.state.take = 0;
    await Promise.all([this.getAllNewTemplate(), this.getClientListAsPromise()]);
    this.value = [];
    this.isShow = false;
    this.isInputFieldEmpty = true;
    this.isChecked = false;
    this.templateBody = '';
    this.subject = 'No Reply - [DOMAIN_NAME]';
    if (this.templateData != null && this.templateData.length > 0) {
      this.templateBody = this.templateData[0]?.Body;
      this.hideTemplateId = this.templateData[0]?.Id;
      this.selectedTemplate = this.templateData[0];
    }
    else {
      this.templateBody = ''
      this.hideTemplateId = 0;
      this.selectedTemplate = null;

    }
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
    this.state.take = 10;

  }

  addTemplate(templateRef: TemplateRef<any>) {
    this.isMaximizedEmail = false
    this.value = [];
    this.isShow = false;
    this.isTemplateInputFieldEmpty = true;
    this.isChecked = false;
    this.Name = '';
    this.Body = '';
    this.clientData = this.allAdminData.map((x: any) => x.FirstName);
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'email-dialog-pane',
      autoFocus: false,
    });
  }

  count(templateRef: TemplateRef<any>) {
    this.clientData = this.allAdminData.map((x: any) => x.FirstName);
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
  }
  submit(form: any) {
    form.controls.Subject.setValue(this.subject);
    form.controls.TemplateBody.setValue(this.templateBody);
    form.controls.Client.setValue(this.value.map((x: any) => x.Id));
    let adminEmailDto = {
      Subject: form.value.Subject,
      CompanyId: this.value.map((x: any) => x.Id),
      TempleteBody: form.value.TemplateBody,
      TempleteName: this.selectedTemplate.Name
    };
    if (form.status == 'VALID' && this.templateBody.trim() != '<p></p>') {
      let data = this.templateBody.replace('<p>', '');
      let data1 = data.replace('</p>', '');
      form.controls.TemplateBody.setValue(data1);
      if (form.controls.TemplateBody.status == 'VALID') {
        this.LoaderService.show();
        this.api.sendAnnouncementEmailforAdmin(adminEmailDto).subscribe(
          (res: any) => {
            if (res['Success'] === true) {
              this.TotalEmails = res.Data.TotalEmails;
              this.TotalSendEmails = res.Data.TotalSendEmails;
              this.TotalUnSendEmails = res.Data.TotalUnSendEmails;
              this.CompantData = res.Data.CompantData;
              this.IsReleaseNote = res.Data.IsReleaseNote;
              if (this.IsReleaseNote)
                this.toaster.success('Release Note Sent Successfully.');
              else
                this.toaster.success('Email process started successfully. Emails are being sent to clients in the background.');
              this.LoaderService.hide();
              this.dialog.closeAll();
              this.count(this.mailCountModal);
              this.getDomain();
            } else {
              this.LoaderService.hide();
              this.toaster.error('Error');
            }
          },
          (_err) => {
            this.LoaderService.hide();
            this.toaster.error('Something went wrong Record not Updated sorry');
          }
        );
      } else {
        this.toaster.error('Template body is required');
      }
    } else {
      this.toaster.error('Please fill all the fields');
    }
  }

  save(form: any) {
    form.controls.Body.setValue(this.Body);
    form.controls.Name.setValue(this.Name);
    let adminEmailTemplateDto = {
      Id: 0,
      Name: form.value.Name,
      Body: form.value.Body,
    };
    if (form.status == 'VALID' && this.Body.trim() != '<p></p>') {
      let data = this.Body.replace('<p>', '');
      let data1 = data.replace('</p>', '');
      form.controls.Body.setValue(data1);
      if (form.controls.Body.status == 'VALID') {
        this.LoaderService.show();
        this.api.adminEmailTemplate(adminEmailTemplateDto).subscribe((res: any) => {
          if (res['Success'] === true) {
            this.LoaderService.hide();
            this.matDialogRef.close();
            this.toaster.success('Template Added Successfully');
            this.getAllNewTemplate(true)
          } else {
            this.LoaderService.hide();
            this.toaster.error('Error');
          }
        },
          (_err) => {
            this.LoaderService.hide();
            this.toaster.error('Something went wrong Record not Updated sorry');
          }
        )
      } else {
        this.toaster.error('Template body is required');
      }
    } else {
      this.toaster.error('Please fill all the fields');
    }

  }

  DeleteTemplate(Item: any) {
    this.IsTemplateClose = Item.Name == "Announcement";
    if (Item.Name == "Announcement") {
      this.toaster.error('The Default email template cannot be deleted');
      return
    }
    this.api.DeleteTemplate(Item).subscribe((res: any) => {
      if (res["Success"] == true) {
        this.toaster.success('Template Delete Successfully');
        this.getAllNewTemplate()
      } else {
        this.toaster.error('Something Went Wrong');
      }
    })
  }

  getTemplateById(Item: any) {
    this.hideTemplateId = Item.Id;
    this.templateName = Item.Name;
    this.isInputFieldEmpty = true
    this.IsTemplateColor = Item.Name == "Announcement";
    this.api.getTemplateById(this.hideTemplateId).subscribe((res: any) => {
      if (res["Success"] == true) {
        this.EmailData = res["Data"].Body
        this.templateBody = this.EmailData;
      }
    });
    this.selectedTemplate = Item;
  }


  getAllNewTemplate(isFromSaveTemplate?: boolean) {
    return new Promise((resolve, reject) => {
      this.IsTemplateClose = this.hideTemplateId === 1;
      this.IsTemplateColor = true;
      this.hideTemplateId = 0;

      this.api.getTemplate().subscribe({
        next: (res: any) => {
          if (res?.Success) {
            this.templateData = res.Data;
            if (isFromSaveTemplate) {
              this.hideTemplateId = this.templateData[this.templateData.length - 1]?.Id ?? 0;
              this.templateBody = this.templateData[this.templateData.length - 1]?.Body ?? '';
              this.selectedTemplate = this.templateData[this.templateData.length - 1];
            }
            else {
              this.hideTemplateId = this.templateData[0]?.Id ?? 0;
              this.templateBody = this.templateData[0]?.Body ?? '';
              this.selectedTemplate = this.templateData[0];

            }

            resolve(true);
          } else {
            reject(new Error('Template fetch failed'));
          }
        },
        error: (err) => {
          reject(new Error('Template fetch failed'));
        }
      });

      //this.getDomain();
    });
  }


  handleKeyDown(event: KeyboardEvent, item: any) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.getTemplateById(item);
    }
  }
  formInIt() {
    this.createMailForm = this.FormBuilder.group({
      Subject: ['', [Validators.required, whitespace]],
      Client: [[], [Validators.required]],
      TemplateBody: ['', [Validators.required, whitespace]],
    });
  }

  emailInit() {
    this.addMailForm = this.FormBuilder.group({
      Name: ['', [Validators.required, whitespace]],
      Body: ['', [Validators.required, whitespace]],
    });
  }
  valueChange(event: any) {
    this.isChecked = this.value.length === this.clientData.length;
  }
  public onClick() {
    this.isChecked = !this.isChecked;
    this.value = this.isChecked ? this.clientData.slice() : [];
  }
  public get toggleAllText() {
    return this.isChecked ? 'Unselect All' : 'Select All';
  }
  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  public changeFilterOperator(operator: 'contains' | 'startsWith'): void {
    this.filterSettings.operator = operator;
  }
  filterFunction(param: any) {
    this.isShow = false;
    let tets: Element | any;
    let data1: Element | any;
    let data = document.getElementById('test');
    tets = data!.children![0].children[0];
    data1 = document.getElementById(tets?.children[0]!.id);
    let show: Element | any;
    if (!!data1) {
      data1.value;
      show = this.clientData.filter(
        (x: any) =>
          x.FirstName.toLowerCase().startsWith(data1.value.toLowerCase()) ||
          x.FirstName.includes(data1.value) ||
          x.FirstName.toLowerCase().includes(data1.value.toLowerCase())
      );
      if (show.length > 0) {
        this.isShow = false;
      } else {
        this.isShow = true;
      }
    } else {
      tets = data!.children[0].children.length - 1;
      data1 = document.getElementById(
        data!.children[0].children[tets].children[0].id
      );
      show = this.clientData.filter(
        (x: any) =>
          x.FirstName.toLowerCase().startsWith(data1.value.toLowerCase()) ||
          x.FirstName.includes(data1.value) ||
          x.FirstName.toLowerCase().includes(data1.value.toLowerCase())
      );
      if (show.length > 0) {
        this.isShow = false;
      } else {
        this.isShow = true;
      }
    }
  }

  //templateBody

  onInputChange(): void {
    // Check if the input field is empty
    if (!!this.templateBody) {
      let data = this.templateBody.replace('<p>', '');
      let data1 = data.replace('</p>', '');
      this.createMailForm.controls['TemplateBody'].setValue(data1);
      if (this.createMailForm.controls['TemplateBody'].status == 'VALID') {
        this.isInputFieldEmpty = true;
      } else {
        this.isInputFieldEmpty = false;
      }
    } else {
      this.isInputFieldEmpty = true;
    }
  }
  InputChange(): void {
    // Check if the input field is empty
    if (!!this.Body) {
      let data = this.Body.replace('<p>', '');
      let data1 = data.replace('</p>', '');
      this.addMailForm.controls['Body'].setValue(data1);
      if (this.addMailForm.controls['Body'].status == 'VALID') {
        this.isTemplateInputFieldEmpty = true;
      } else {
        this.isTemplateInputFieldEmpty = false;
      }
    } else {
      this.isTemplateInputFieldEmpty = true;
    }
  }
  insertTextAtCursor(text: string) {
    this.kendoEditor.exec('insertText', { text: '#CURSOR#' });
    this.kendoEditor.value = this.kendoEditor.value.replace(/#CURSOR#/, text);
    this.templateBody = this.kendoEditor.value;
  }



  onClientFilterChange(): void {
    this.state.skip = 0;
    // The loaded full list was fetched for the previous plan/account type/status filters
    this.resetFullList();
    this.applySortOrLoad();
  }

  /** Reloads the grid's rows - the whole list again while it is in full-list mode (see applySortOrLoad()). */
  getDomain() {
    if (this.fullListActive) {
      this.resetFullList();
      this.applySortOrLoad();
      return;
    }
    this.fetchDomainPage();
  }

  private fetchDomainPage(): void {
    this.markStateApplied();
    const requestState = withoutGroups(this.state);
    if (environment.environmentType === "plusdoctor") {
      this.fetchDomainData(this.api.GetPlusDoctorDomain(requestState, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus));
    } else {
      this.fetchDomainData(this.api.GetPlusPhysioDomain(requestState, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus));
    }
  }

  private getDomainAsPromise(): Promise<void> {
    const observable = environment.environmentType === "plusdoctor"
      ? this.api.GetPlusDoctorDomain(this.state, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus)
      : this.api.GetPlusPhysioDomain(this.state, JSON.stringify(this.searchString.trim()), this.selectedPlan, this.selectedAccountType, this.selectedStatus);

    return new Promise<void>((resolve) => {
      observable.subscribe({
        next: (res: any) => {
          if (res?.Data) {
            this.allAdminData = res.Data.map((e: any) => this.transformCompany(e));
          } else {
            this.allAdminData = [];
          }
          resolve();
        },
        error: () => resolve()
      });
    });
  }

  private getClientListAsPromise(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.api.GetClientList(environment.environmentType).subscribe({
        next: (res: any) => {
          if (res?.Data) {
            this.clientData = Object.entries(res.Data)
              .filter(([, firstName]) => !!firstName)
              .map(([id, firstName]) => ({ Id: +id, FirstName: firstName as string }));
          } else {
            this.clientData = [];
          }
          resolve();
        },
        error: () => resolve()
      });
    });
  }

  private fetchDomainData(observable: Observable<any>): void {
    this.LoaderService.show();
    if (this.formValue != undefined) {
      this.formValue.controls['CompanyName'].disable();
    }

    observable.subscribe({
      next: (res: any) => this.handleDomainResponse(res),
      error: (err: any) => {
        this.LoaderService.hide();
        console.error(`Error fetching domain data:`, err);
        this.toaster.error('Something went wrong while fetching record');
      }
    });
  }

  private handleDomainResponse(res: any): void {
    if (res?.Data) {
      this.allAdminData = res.Data.map((e: any) => this.transformCompany(e));

      forkJoin(
        this.allAdminData.map((company: any) =>
          forkJoin({
            usage: this.api.getUsageOverview(company.Id),
            addOns: this.api.getAddOnDetails(company.Id)
          })
        )
      ).subscribe((results: any) => this.applyUsageResults(results));

      this.total = res.Total;
      this.loadData();
    } else {
      this.allAdminData = [];
      this.total = 0;
      this.loadData();
    }
    this.LoaderService.hide();
  }

  private transformCompany(e: any): any {
    e.CreatedDate = e.CreatedDate.split('T')[0];
    e.CreatedDate = this.datePipe.transform(new Date(e.CreatedDate), 'dd-MM-yyyy');

    const data = this.currencyList.filter((x: any) => x.Id == e.CurrencyId);
    if (data.length > 0) {
      e.Country = data[0].CountryName;
    }

    e.IsWhatsAppIntegration = e.IsWhatsAppIntegration ? 'Subscribed' : 'Not Subscribed';
    return e;
  }

  private applyUsageResults(results: any[]): void {
    results.forEach((result: any, index: any) => {
      const company = this.allAdminData[index];
      const latestUsage = result.usage?.Data?.[0] ?? null;

      if (company.AddOnDetails?.length > 0) {
        company.AddOnDetails = company.AddOnDetails.map((detail: any) =>
          this.mapAddonUsage(detail, latestUsage)
        );
      }
    });
    this.loadData();
  }

  private mapAddonUsage(detail: any, latestUsage: any): any {
    let used = 0;
    if (latestUsage) {
      if (detail.TypeName === 'User') used = latestUsage.TotalAddonUsers;
      else if (detail.TypeName === 'Location') used = latestUsage.TotalLocations;
      else if (detail.TypeName === 'Patients') used = latestUsage.TotalPatients;
      else if (detail.TypeName === 'Storage') used = latestUsage.TotalStorage;
    }
    return { ...detail, addonUsage: used };
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.setGridHeight();
  }

  setGridHeight(): void {
    const headerHeight = 70; // Adjust according to your header and other elements
    const footerHeight = 61;
    const otherElementsHeight = 125;
    this.gridHeight = window.innerHeight - headerHeight - footerHeight - otherElementsHeight;
  }

  clearList() {
    this.searchString = '';
    this.resetFullList();
    this.sort = [];
    this.state.sort = [];
    this.applySortOrLoad();
  }

  onColumnVisibilityChange(columnEvent: GridComponent) {
    let currentDate = this.datePipe.transform(new Date(), "yyyy-MM-dd");
    const data = columnEvent.columns["_results"]
      .filter((x: any) => x?.hidden != true)
      .map((x: any) => x.field);
    const jsonData = JSON.stringify(data);
    const reqBody = {
      Id: 0,
      PersistString: jsonData,
      TableName: "Client Details",
      DomainName: localStorage.getItem("UserName"),
      CreatedOn: currentDate,
      CreatedBy: localStorage.getItem("UserName"),
    };
    this.api.AdminGridSettingAddData(reqBody).subscribe((res: any) => {
      if (res["Success"]) {
        this.toaster.success("Changes Saved");
      } else {
        this.toaster.error("Changes Not Saved");
      }
    });
  }

  ngAfterViewInit(): void {
    this.api.GetDomainName(localStorage.getItem("UserName"), this.TableName).subscribe((res: any) => {
      const data = res["Data"];
      if (data && data.PersistString) {
        this.displayColumns = JSON.parse(data.PersistString);
        const grid = this.gridKendo.columns["_results"];
        grid.forEach((column: any) => {
          if (!this.displayColumns.includes(column.field)) {
            column.hidden = true;
          }
        });
      }
    });
  }

  openAddOnDialog(data: any, addOnTemplate: TemplateRef<any>) {
    if (data.IsAutoRenew) {
      this.toaster.error('Auto-Renewal is active. Ask the user to purchase the Add-on directly.');
      return;
    }

    let expiryDate: Date = new Date(data.PlanExpiryDate);
    let currentDate: Date = new Date();

    const compYear = expiryDate.getFullYear();
    const compMonth = expiryDate.getMonth();
    const compDay = expiryDate.getDate();

    const currYear = currentDate.getFullYear();
    const currMonth = currentDate.getMonth();
    const currDay = currentDate.getDate();

    if (compYear < currYear ||
      (compYear === currYear && compMonth < currMonth) ||
      (compYear === currYear && compMonth === currMonth && compDay < currDay)) {
      this.toaster.error('The domain plan has expired. Add-ons cannot be added at this time.');
      return;
    }

    this.companyId = data.Id;

    this.api.getActiveAddOnDetails(this.companyId).subscribe((res: any) => {
      if (res?.Success) {
        const activeAddOns = res.Data ?? [];
        const addOnCountMap = activeAddOns.reduce((acc: any, item: any) => {
          acc[item.AddOnTypeId] = (acc[item.AddOnTypeId] ?? 0) + (item.AddOnCount ?? 0);
          return acc;
        }, {});

        this.addOns = this.originalAddOns.map(addOn => ({
          ...addOn,
          quantity: 0,
          previousQuantity: addOnCountMap[addOn.id] ?? 0
        }));
      }
      else {
        this.addOns = this.originalAddOns.map(addOn => ({
          ...addOn,
          quantity: 0,
          previousQuantity: 0
        }));
      }
    });

    this.matDialogRef = this.dialog.open(addOnTemplate, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
  }

  increment(item: any) {
    if (item.name === 'Patients') {
      item.quantity = (item.quantity || 0) + 1000;
    } else if (item.name === 'Storage') {
      item.quantity = (item.quantity || 0) + 100;
    } else {
      item.quantity = (item.quantity || 0) + 1;
    }
  }

  decrement(item: any) {
    if (item.name === 'Patients' && item.quantity > 0) {
      item.quantity -= 1000;
    } else if (item.name === 'Storage' && item.quantity > 0) {
      item.quantity -= 100;
    } else if (item.name !== 'Patients' && item.name !== 'Storage' && item.quantity > 0) {
      item.quantity -= 1;
    }
  }

  getAddOnTotal(): number {
    return this.addOns.reduce((sum, item) => sum + (item.id == 3 ? (item.quantity / 1000 * item.price) : (item.id == 4 ? item.quantity / 100 * item.price : item.quantity * item.price)), 0);
  }

  submitAddOn() {
    const loginData = localStorage.getItem("loginData");
    if (!loginData) return;

    this.LoaderService.show();
    const userId = JSON.parse(loginData).Id;
    var addOns = this.addOns.filter(i => i.quantity > 0)

    if (addOns.length === 0) {
      this.LoaderService.hide();
      this.toaster.error('Please select at least one add-on.');
      return;
    }

    var payload: any = [];
    addOns.forEach(element => {
      payload.push({
        CompanyId: this.companyId,
        AddOnTypeId: element.id,
        AddOnCount: element.quantity,
        AddOnAmount: (element.id == 3 ? (element.quantity / 1000 * element.price) : (element.id == 4 ? element.quantity / 100 * element.price : element.quantity * element.price)),
        AddOnBaseAmount: element.price,
        CreatedBy: userId
      })
    })

    this.api.createAddOnDetails(payload).subscribe((res: any) => {
      if (res["Success"]) {
        this.toaster.success('Add-on configuration saved successfully.');
        this.matDialogRef.close();
        this.getDomain();
        this.loadData();
        this.LoaderService.hide();
      } else {
        this.LoaderService.hide();
        this.toaster.error('Something went wrong');
      }
    });
  }

  setItemPrice(event: any, item: any) {
    var itemPrice = event?.target?.value ? parseFloat(event.target.value) : 0
    this.addOns = this.addOns.map(addOn =>
      addOn.id === item.id ? { ...addOn, price: itemPrice } : addOn)
  }

  onlyPositiveNumbers(event: any) {
    let input: string;

    // Handle keyboard typing
    if (event.type === 'keypress') {
      input = event.key;
      if (!/^\d$/.test(input)) {
        event.preventDefault();
      }
    }

    // Handle pasting
    if (event.type === 'paste') {
      input = event.clipboardData?.getData('text') ?? '';
      if (!/^\d+$/.test(input)) {
        event.preventDefault();
      }
    }
  }

  openChangePlanDialog(data: any, templateRef: TemplateRef<any>) {
    if (data.IsAutoRenew) {
      this.toaster.error('Please disable auto-renewal before changing the plan.');
      return;
    }

    this.isPlanYearly = false;
    this.selectedRecord = data;
    this.isSendInvoice = true;
    this.selectedPlanId = data.PlanId;
    this.api.getSubscriptionDetails(this.selectedRecord?.Id).subscribe({
      next: (res: any) => {
        if (res["Success"] && res["ResponseCode"] === 200 && res["Data"]) {
          const subData = res["Data"][0];
          if (subData?.TransactionCurrencyId) {
            this.selectedCurrencyId = this.toPickerCurrencyId(subData.TransactionCurrencyId);
            const currencyCode = this.getCurrencyCodeById(this.selectedCurrencyId);
            this.updatePlanPrices(currencyCode);
          }
          if (!this.selectedPlanId && subData?.PlanId) {
            this.selectedPlanId = subData.PlanId;
          }
        }
      }
    });

    this.expiryDate = new Date();
    if (this.isPlanYearly) {
      // Add 365 days
      this.expiryDate.setDate(this.expiryDate.getDate() + 365);
    } else {
      // Add 30 days
      this.expiryDate.setDate(this.expiryDate.getDate() + 30);
    }

    const currencyCode = this.getCurrencyCodeById(this.selectedCurrencyId);
    this.plans = [
      {
        id: SubscriptionPlanType.BasicPlan,
        name: 'Basic Plan',
        monthlyPrice: this.planPrices[SubscriptionPlanType.BasicPlan][currencyCode].monthly,
        annualPrice: this.planPrices[SubscriptionPlanType.BasicPlan][currencyCode].annual,
        isShowAddOn: false,
        expriyDate: this.expiryDate,
      },
      {
        id: SubscriptionPlanType.StandardPlan,
        name: 'Standard Plan',
        monthlyPrice: this.planPrices[SubscriptionPlanType.StandardPlan][currencyCode].monthly,
        annualPrice: this.planPrices[SubscriptionPlanType.StandardPlan][currencyCode].annual,
        isShowAddOn: false,
        expriyDate: this.expiryDate,
      },
      {
        id: SubscriptionPlanType.PremiumPlan,
        name: 'Premium Plan',
        monthlyPrice: this.planPrices[SubscriptionPlanType.PremiumPlan][currencyCode].monthly,
        annualPrice: this.planPrices[SubscriptionPlanType.PremiumPlan][currencyCode].annual,
        isShowAddOn: false,
        expriyDate: this.expiryDate,
      },
      {
        id: SubscriptionPlanType.EnterprisePlan,
        name: 'Enterprise Plan',
        monthlyPrice: this.planPrices[SubscriptionPlanType.EnterprisePlan][currencyCode].monthly,
        annualPrice: this.planPrices[SubscriptionPlanType.EnterprisePlan][currencyCode].annual,
        isShowAddOn: false,
        expriyDate: this.expiryDate,
      }
    ].map(plan => ({ ...plan, discountValue: null as number | null, discountType: 'percent' as DiscountType }));

    if (data.AddOnDetails && data.AddOnDetails.length > 0) {
      let addOnAmount = data.AddOnDetails.reduce(
        (n: number, { AddOnAmount }: { AddOnAmount: number }) => n + AddOnAmount,
        0
      );

      this.plans = this.plans.map(plan => {
        if ((plan.id === SubscriptionPlanType.StandardPlan || plan.id === SubscriptionPlanType.PremiumPlan) && plan.id >= this.selectedPlanId) {
          const monthly = parseInt(plan.monthlyPrice.replace(/[^\d]/g, '')) || 0;
          const annual = parseInt(plan.annualPrice.replace(/[^\d]/g, '')) || 0;

          return {
            ...plan,
            monthlyPrice: `${monthly + addOnAmount}`,
            annualPrice: `${annual + addOnAmount}`,
            isShowAddOn: true
          };
        }
        return plan;
      });
    }

    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
      width: '900px',
    });
  }

  changePlan(plan: any) {
    this.newPlan = plan;
    this.newPlan.expriyDate = this.expiryDate;

    const isPaidPlan = this.newPlan.id > SubscriptionPlanType.BasicPlan;
    const price = this.isPlanYearly ? this.newPlan.annualPrice : this.newPlan.monthlyPrice;
    const planPrice = isPaidPlan ? price : 0;

    if (planPrice === '' || planPrice === null || planPrice === undefined || planPrice === '-' || Number.parseFloat(planPrice) < 0) {
      this.toaster.error('Please enter a valid price.');
      return;
    }

    const discountProblem = isPaidPlan ? this.planDiscountError(this.newPlan) : null;
    if (discountProblem) {
      this.toaster.error(discountProblem);
      return;
    }

    this.confirmationDialog = this.dialog.open(this.changePLanConfirmationDialog, {
      disableClose: true,
      panelClass: "popup_custom_width_small",
      autoFocus: false,
    });
  }

  updatePlan() {
    // Like a coupon, the discount is taken off before saving - the transaction and invoice hold what the client pays,
    // and DiscountPercentage records the discount for the Transactions grid
    const isPaidPlan = this.newPlan.id > SubscriptionPlanType.BasicPlan;
    let price = isPaidPlan ? (this.planTotal(this.newPlan) ?? 0) : 0;
    const discount = isPaidPlan
      ? discountPercentage(this.planPrice(this.newPlan), this.newPlan.discountValue, this.newPlan.discountType ?? 'percent')
      : null;

    let stripePaymentModel = {
      CompanyId: this.selectedRecord?.Id,
      Amount: price,
      TotalAmount: price,
      DiscountPercentage: discount,
      UserId: 0,
      TransactionTypeId: 2,
      //set 0 means dont't use SubscriptionPlanId from frontend.
      SubscriptionPlanId: 0,
      PlanId: this.newPlan?.id,
      IsMonth: !this.isPlanYearly,
      IPAddress: this.ipAddress,
      ExpiryDate: this.newPlan?.expriyDate ? this.formatDateLocal(this.newPlan?.expriyDate) : null,
      TransactionCurrencyId: this.selectedCurrencyId,
      IsSendInvoice: this.isSendInvoice,
      IsAdminRedirect: true
    }

    this.LoaderService.show();
    this.api.AddTransactionDetails(stripePaymentModel).subscribe({
      next: (res: any) => {
        if (res["Success"]) {
          this.toaster.success('Plan changed successfully.');
          this.matDialogRef.close();
          this.confirmationDialog.close();
          this.LoaderService.hide();
          this.sendPlanChangeEmail();
          this.forceLogoutAllUsers();

          //If plan is upgraded from basic/standard to premium/enterprise then create domain
          if (stripePaymentModel.SubscriptionPlanId > 6 && this.selectedRecord?.SubscriptionPlanId <= 6) {
            this.CreateDomain(stripePaymentModel.CompanyId);
          } else {
            this.getDomain();
            this.loadData();
          }
        } else {
          this.LoaderService.hide();
          this.confirmationDialog.close();
          this.toaster.error('Something went wrong while changing the plan.');
        }
      }
    });
  }

  formatDateLocal(dateString: string): string {
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  closeConfirmationDialog() {
    this.confirmationDialog.close();
  }

  sendPlanChangeEmail() {
    let selectedRec = this.selectedRecord;
    let newPlan = this.newPlan;
    let userName = selectedRec.FirstName + ' ' + (selectedRec.LastName || '');
    let action = newPlan.id > selectedRec.PlanId ? 'Upgraded' : (newPlan.id < selectedRec.PlanId ? 'Downgraded' : 'Renewed');

    let adminEmailDto = {
      Subject: 'Subscription Update – Plan Changed Successfully',
      CompanyId: [selectedRec?.Id],
      TempleteBody: `<p>Hi&nbsp;${userName},</p>
    <p>Your plan has been successfully <b>${action}</b>, effective today.</p>
    <p><b>Plan Name:</b> ${newPlan.name}<br><b>Status:</b> ${action}</p>
    <p>Thank you for choosing PlusPhysio. You can manage your subscription anytime from your subscription settings.<br>
    If you have any questions or suggestions, please feel free to contact us at [SUPPORT_NAME] or call us at <b>[CONTACT_NUMBER]</b>.</p>
    </p>
    <p><b>Best regards,</b>
    <br>
    <b>The PlusPhysio Team</b></p>`,
    };

    this.api.sendAnnouncementEmailforAdmin(adminEmailDto).subscribe({
      error: () => { }
    });
  }

  forceLogoutAllUsers() {
    this.api.logoutAllUsers(this.selectedRecord?.Id).subscribe({
      error: () => { }
    });
  }

  openMastermigrationDialog(_data: any, templateRef: TemplateRef<any>) {
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
  }
  openResetPasswordDialog(data: any) {
    this.selectedUser = data;

    this.matDialogRef = this.dialog.open(this.changeResetPasswordDialog, {
      disableClose: true,
      autoFocus: false,
    });

    this.matDialogRef.afterOpened().subscribe({
      next: () => {
        this.resetInit();
      }
    });
  }
  onIconKeyDown(event: KeyboardEvent) {
    const key = event.key;
    // activate on Enter or Space (Space: prevent page scrolling)
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();           // important for Space
      this.toggleNewPasswordVisibility();
    }
  }
  onIconKeyDownConfirm(event: KeyboardEvent) {
    const key = event.key;
    // activate on Enter or Space (Space: prevent page scrolling)
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();           // important for Space
      this.toggleConfirmPasswordVisibility();
    }
  }
  toggleNewPasswordVisibility(): void {
    this.hideNewPassword = !this.hideNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.hideConfirmPassword = !this.hideConfirmPassword;
  }
  control(controlName: string): AbstractControl | null {
    return this.form.get(controlName);
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.control(controlName);
    if (!control) return false;
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  addMasterMigration() {
    this.IsDisable = true;
    this.LoaderService.show();
    this.api.addMasterMigration().subscribe({
      next: (res: any) => {
        this.LoaderService.hide();
        if (res && res.Success) {
          this.toaster.success('Migration Run Successfully..');
          this.matDialogRef.close();
        } else {
          this.toaster.error(res?.Message || 'Something went wrong');
        }
        setTimeout(() => {
          this.IsDisable = false;
        }, 2000);
      },
      error: (_err: any) => {
        this.LoaderService.hide();
        this.IsDisable = false;
        this.toaster.error('Something went wrong');
      },
    });
  }

  updatePlanPrice(index: number, newValue: any) {
    if (this.isPlanYearly) {
      this.plans[index].annualPrice = newValue?.target?.value;
    } else {
      this.plans[index].monthlyPrice = newValue?.target?.value;
    }
  }

  /** The price entered for a plan for the selected billing period - NaN while none is entered. */
  planPrice(plan: any): number {
    return Number.parseFloat(this.isPlanYearly ? plan.annualPrice : plan.monthlyPrice);
  }

  hasPlanDiscount(plan: any): boolean {
    return Number(plan?.discountValue) > 0;
  }

  planDiscountError(plan: any): string | null {
    return discountError(this.planPrice(plan), plan?.discountValue, plan?.discountType ?? 'percent');
  }

  /** What the client pays for a plan - its price less the discount; null while no price is entered. */
  planTotal(plan: any): number | null {
    const price = this.planPrice(plan);
    return Number.isFinite(price) ? discountedPrice(price, plan?.discountValue, plan?.discountType ?? 'percent') : null;
  }
  getStatusColor(status: string): string {
    switch (status) {
      case 'Registered':
        return '#D95922';
      case 'OTPVerified':
        return '#044B4F';
      case 'LoggedIn':
        return 'green';
      default:
        return 'black';
    }
  }

  /** The label the Status dropdown shows for a status value - used by its group header. */
  getStatusText(status: string): string {
    return this.dropdownValues.find(option => option.value === status)?.text ?? status;
  }

  // IsNewPlatformMigrated is computed live by the backend (IfourCompanyMasterService.GetPlusPhysioDomain,
  // called from api/Company/GetPlusPhysioDomain) via the same check UserService.GetNewPlatformMigrationStatusAsync
  // uses for a logged-in tenant: unitOfWork.UserRepository.IsMigrationVersionAppliedAsync(Constant.NewPlatformMigrationVersion)
  // run against that company's own database. Do not derive this from MigrationVersionInfo - it's only
  // ever set when an admin explicitly runs a migration for that company from this panel, so it's null
  // for every company that has never had one triggered here even if their DB is actually migrated.
  isNewPlatformMigrated(dataItem: any): boolean {
    return !!dataItem?.IsNewPlatformMigrated;
  }

  whatsappFormInit() {
    this.whatsappForm = this.FormBuilder.group({
      Provider: ['Ifour'],
      InstanceKey: ['', [Validators.required, whitespace]],
      IsGloble: [true], // true = Global, false = Location-wise
      LocationId: [null],
      ExpiryDate: [null, Validators.required],
      Amount: [0, [Validators.required, Validators.min(0)]],
    });

    this.whatsappForm.get('Provider')?.valueChanges.subscribe({
      next: (value) => {
        const instanceKeyControl = this.whatsappForm.get('InstanceKey');
        if (value === 'SmsIdea') {
          instanceKeyControl?.setValidators([Validators.required, whitespace as any]);
        } else {
          instanceKeyControl?.clearValidators();
        }
        instanceKeyControl?.updateValueAndValidity();
      }
    });

    // Handle LocationType changes (true = Global, false = Location-wise)
    this.whatsappForm.get('IsGloble')?.valueChanges.subscribe({
      next: (value) => {
        const locationIdControl = this.whatsappForm.get('LocationId');
        if (value === false) { // Location-wise
          locationIdControl?.setValidators([Validators.required]);
        } else { // Global (true)
          locationIdControl?.clearValidators();
          // Set to first location when Global is selected
          if (this.locationList.length > 0) {
            locationIdControl?.setValue(this.locationList[0].Id);
          }
        }
        locationIdControl?.updateValueAndValidity();
      }
    });
  }

  async openWhatsappEnableDialog(data: any, templateRef: TemplateRef<any>) {
    this.isWhatsappSubmitted = false;
    this.resetWhatsappForm();
    this.selectedRecord = data;
    this.whatsappForm.patchValue({ Provider: 'Ifour' });

    this.LoaderService.show();
    try {
      await this.getLocations(data.Id);

      this.matDialogRef = this.dialog.open(templateRef, {
        disableClose: true,
        autoFocus: false,
      });
    } finally {
      this.LoaderService.hide();
    }
  }

  openAIAssistantDialog(data: any, templateRef: TemplateRef<any>) {
    this.selectedRecord = data;

    this.api.getGeneralSetting(this.selectedRecord.Id, this.aiKeyName).subscribe((res: any) => {
      this.isAIEnabled = res?.Data?.Value?.toLowerCase() === 'true';
    })

    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      autoFocus: false,
    });
  }

  updateAIAssistantStatus() {
    this.LoaderService.show();

    const settingDto = {
      Key: this.aiKeyName,
      Value: this.isAIEnabled.toString().toLowerCase()
    };

    this.api.updateGeneralSettingByDomain(this.selectedRecord.Id, settingDto).subscribe({
      next: (res: any) => {
        if (res.Success) {
          this.toaster.success(`AI Assistant ${this.isAIEnabled ? 'Enabled' : 'Disabled'} Successfully`);
          this.matDialogRef.close();
        } else {
          this.toaster.error('Failed to update AI Assistant status');
        }
        this.LoaderService.hide();
        this.cd.detectChanges();
      },
      error: (err) => {
        this.toaster.error(err?.message);
        this.LoaderService.hide();
        this.cd.detectChanges();
      }
    });
  }

  getLocations(companyId: any): Promise<void> {
    return firstValueFrom(this.api.getLocations(companyId)).then((res) => {
      if (res.Success) {
        this.locationList = res['Data'];
        if (this.locationList.length > 0) {
          this.whatsappForm.get('LocationId')?.setValue(this.locationList[0].Id);
        }
      } else {
        this.locationList = [];
      }
      this.cd.detectChanges();
    });
  }

  resetWhatsappForm() {
    this.locationList = [];
    this.whatsappForm.reset({
      Provider: 'SendClean',
      InstanceKey: null,
      IsGloble: true, // true = Global
      LocationId: null,
      ExpiryDate: null
    });
  }

  addWhatsappInstance() {
    this.isWhatsappSubmitted = true;
    if (this.whatsappForm.invalid) {
      Object.keys(this.whatsappForm.controls).forEach(key => {
        const control = this.whatsappForm.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });
      return;
    }
    let obj = {
      CompanyId: this.selectedRecord.Id,
      LocationId: this.whatsappForm.value.LocationId,
      IPAddress: this.ipAddress,
      InstanceKey: this.whatsappForm.value.InstanceKey,
      Provider: this.whatsappForm.value.Provider,
      ExpiryDate: this.formatDateLocal(this.whatsappForm.value.ExpiryDate),
      Amount: this.whatsappForm.value.Amount,
      IsGloble: this.whatsappForm.value.IsGloble
    };

    this.LoaderService.show();
    this.api.enableWhatsappInstance(obj).subscribe({
      next: (res: any) => {
        if (res["Success"] === true) {
          this.toaster.success('Whatsapp Instance Created Successfully..');
          this.matDialogRef.close();
          this.whatsappForm.reset();
        } else if (res["ResponseCode"] == 555) {
          this.toaster.error("Whatsapp Instance is not valid.");
        } else if (res["ResponseCode"] == 435) {
          this.toaster.error("Company Not Found.");
        }
        this.LoaderService.hide();
      }, error: (err: any) => {
        this.toaster.error('Something went wrong Record not updated sorry');
        this.LoaderService.hide();
      }
    });
  }

  onPlanToggle(event: any) {
    if (event.checked) {
      this.isPlanYearly = true;
      this.expiryDate = new Date(this.today);
      this.expiryDate.setDate(this.expiryDate.getDate() + 365);
    } else {
      this.isPlanYearly = false;
      this.expiryDate = new Date(this.today);
      this.expiryDate.setDate(this.expiryDate.getDate() + 30);
    }

  }

  onExpiryDateChange(index: number, event: any) {
    this.plans[index].expriyDate = event.value;
    this.expiryDate = event.value;
  }
  onTemplateChange(item: any) {
    item?.Id ? this.getTemplateById(item) : this.removeTemplate();
  }

  /** Release notes are shown as-is in the client's "What's new" popup (never mail-merged),
   *  so the [CLIENT_NAME]-style keyword list is hidden while this template is selected. */
  get isReleaseNoteSelected(): boolean {
    return this.selectedTemplate?.Name === this.RELEASE_NOTE_TEMPLATE_NAME;
  }

  removeTemplate() {
    this.selectedTemplate = null;
    this.hideTemplateId = null;
    this.templateName = '';
    this.isInputFieldEmpty = false;
    this.IsTemplateColor = false;
    this.EmailData = '';
    this.templateBody = '';
  }

  resetInit() {
    this.form = this.FormBuilder.group(
      {
        NewPassword: ["", [Validators.required,]],
        ConfirmPassword: ["", [Validators.required]],
      },
      {
        validators: Validation.match('NewPassword', 'ConfirmPassword'), // call the method
      }
    );
  }

  async submitResetForm() {

    if (this.form.invalid) {
      this.form.markAllAsTouched();  // highlight errors
      return;                        // stop execution
    }
    const passwordObj = {
      Domain: this.selectedUser.Domain,
      Email: this.selectedUser.Email,
      NewPassword: this.form.value.NewPassword,
      ConfirmPassword: this.form.value.ConfirmPassword,
    };

    this.loading = true;
    this.LoaderService.show();

    passwordObj.NewPassword = this.encryptionService.encrypt(passwordObj?.NewPassword);
    passwordObj.ConfirmPassword = this.encryptionService.encrypt(passwordObj?.ConfirmPassword);

    this.api.resetPassword(passwordObj).subscribe({
      next: (response: any) => {
        if (response.Success) {
          this.close();
          if (response.ResponseCode === 200) {
            this.toaster.success('Password Changed Successfully.');
          }
        } else {
          switch (response.ResponseCode) {
            case 414:
              this.toaster.error('Password reset failed.');
              break;
            case 411:
              this.toaster.error('User is Inactive.');
              break;
            case 409:
              this.toaster.error('User not found.');
              break;
            default:
              this.toaster.error('Something went wrong, record not updated.');
              break;
          }
        }

        this.loading = false;
        this.LoaderService.hide();
      },
      error: (err) => {
        this.toaster.error('Something went wrong. Please try again later.');
        this.loading = false;
        this.LoaderService.hide();
      }
    });
  }

  close() {
    this.dialog.closeAll();
  }



  planLimitInit() {
    this.planLimitForm = this.FormBuilder.group({
      ServiceItem: ['', [Validators.required, whitespace]],
      PatientCustomField: ['', [Validators.required, whitespace]],
      Reference: ['', [Validators.required, whitespace]],
    });
  }

  async openPlanLimitDialog(dataItem: any) {
    this.resetPlanLimitForm();
    this.selectedUser = dataItem;
    const loaded = await this.getPlanlimits(dataItem.Id);
    if (!loaded) {
      return;
    }
    this.dialog.open(this.planLimitDialog, {
      disableClose: true,
      autoFocus: false,
    });
  }

  resetPlanLimitForm() {
    this.planLimitForm.reset({
      ServiceItem: '',
      PatientCustomField: '',
      Reference: ''
    });
  }

  /** Loads the company's plan limits into the form. Resolves to false when the load failed, so the caller can skip opening the dialog. */
  async getPlanlimits(Id: number): Promise<boolean> {
    this.loading = true;
    this.LoaderService.show();
    try {
      const response: any = await firstValueFrom(this.api.getPlanLimit(Id));
      if (!response?.Success) {
        this.toaster.error('Unable to load plan limits. Please try again later.');
        return false;
      }
      this.planLimitForm.patchValue({
        ServiceItem: response?.Data?.ServiceItem,
        PatientCustomField: response?.Data?.PatientCustomField,
        Reference: response?.Data?.Reference
      });
      return true;
    } catch {
      this.toaster.error('Unable to load plan limits. Please try again later.');
      return false;
    } finally {
      this.loading = false;
      this.LoaderService.hide();
    }
  }

  updatePlanLimit() {
    if (!this.planLimitForm.valid) {
      return;
    }
    this.loading = true;
    this.LoaderService.show();
    let payload = {
      CompanyId: this.selectedUser.Id,
      ServiceItem: this.planLimitForm.value.ServiceItem,
      PatientCustomField: this.planLimitForm.value.PatientCustomField,
      Reference: this.planLimitForm.value.Reference,
    };
    this.api.updatePlanLimit(payload).subscribe({
      next: (response: any) => {
        if (response.Success) {
          this.close();
          if (response.ResponseCode === 200) {
            this.toaster.success('Plan Limit updated Successfully.');
          }
        } else {
          this.toaster.error('Something went wrong, record not updated.');
        }
        this.loading = false;
        this.LoaderService.hide();
      },
      error: (err) => {
        this.toaster.error('Something went wrong. Please try again later.');
        this.loading = false;
        this.LoaderService.hide();
      }
    });
  }

  creditLimitInit() {
    this.creditLimitForm = this.FormBuilder.group({
      CreditLimit: ['', [Validators.required, whitespace]],
    });
  }

  async openCreditLimitDialog(dataItem: any) {
    this.resetCreditLimitForm();
    this.selectedUser = dataItem;
    await this.getCreditLimits(dataItem.Id);
    this.dialog.open(this.creditLimitDialog, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
      width: '400px',
    });
  }

  resetCreditLimitForm() {
    this.creditLimitForm.reset({
      CreditLimit: '',
    });
  }

  async getCreditLimits(Id: number) {
    this.loading = true;
    this.LoaderService.show();
    const response: any = await firstValueFrom(this.api.getCreditLimit(Id));
    if (response.Success) {
      this.remainingCreditLimit = response?.Data?.Balance;
    } else {
      this.toaster.error('Something went wrong, record not updated.');
    }
    this.loading = false;
    this.LoaderService.hide();
  }

  updateCreditLimit() {
    if (!this.creditLimitForm.valid) {
      return;
    }
    this.loading = true;
    this.LoaderService.show();
    let payload = {
      CompanyId: this.selectedUser.Id,
      Balance: this.creditLimitForm.value.CreditLimit,
    };
    this.api.updateCreditLimit(payload).subscribe({
      next: (response: any) => {
        if (response.Success) {
          this.close();
          if (response.ResponseCode === 200) {
            this.toaster.success('Credit Limit updated Successfully.');
          }
        } else {
          this.toaster.error('Something went wrong, record not updated.');
        }
        this.loading = false;
        this.LoaderService.hide();
      },
      error: (err) => {
        this.toaster.error('Something went wrong. Please try again later.');
        this.loading = false;
        this.LoaderService.hide();
      }
    });
  }
  toggleMaximize(): void {
    const overlayPane = document.querySelector('.cdk-overlay-pane:has(mat-dialog-container)');
    if (!(overlayPane instanceof HTMLElement)) return;
    const dialogElement = document.querySelector('mat-dialog-container');
    if (!(dialogElement instanceof HTMLElement)) return;
    const contentElement = dialogElement?.querySelector('.mat-dialog-content');
    if (!(contentElement instanceof HTMLElement)) return;
    this.mapService.maximizeMinimize();
    if (!overlayPane || !dialogElement) return;
    this.isMaximized = !this.isMaximized;
    if (this.isMaximized) {
      // Save original styles only once
      if (!this.originalOverlayStyle.width) {
        this.originalOverlayStyle = {
          position: overlayPane.style.position,
          top: overlayPane.style.top,
          left: overlayPane.style.left,
          margin: overlayPane.style.margin,
          width: overlayPane.style.width,
          height: overlayPane.style.height,
          maxWidth: overlayPane.style.maxWidth,
          maxHeight: overlayPane.style.maxHeight
        };

        this.originalDialogStyle = {
          width: dialogElement.style.width,
          height: dialogElement.style.height,
          maxHeight: dialogElement.style.maxHeight
        };
      }

      // Apply maximize styles
      overlayPane.style.position = 'fixed';
      overlayPane.style.top = '0';
      overlayPane.style.left = '0';
      overlayPane.style.margin = '0';
      overlayPane.style.setProperty('width', '100vw', 'important');
      overlayPane.style.setProperty('height', '100vh', 'important');
      overlayPane.style.setProperty('max-width', '100vw', 'important');
      overlayPane.style.setProperty('max-height', '100vh', 'important');

      dialogElement.style.setProperty('width', '100vw', 'important');
      dialogElement.style.setProperty('max-width', '100vw', 'important');
      contentElement.style.maxHeight = '72vh';
    } else {
      // Restore original styles
      overlayPane.style.position = this.originalOverlayStyle.position ?? '';
      overlayPane.style.top = this.originalOverlayStyle.top ?? '';
      overlayPane.style.left = this.originalOverlayStyle.left ?? '';
      overlayPane.style.margin = this.originalOverlayStyle.margin ?? '';
      overlayPane.style.width = this.originalOverlayStyle.width ?? '';
      overlayPane.style.height = this.originalOverlayStyle.height ?? '';
      overlayPane.style.maxWidth = this.originalOverlayStyle.maxWidth ?? '';
      overlayPane.style.maxHeight = this.originalOverlayStyle.maxHeight ?? '';

      dialogElement.style.width = this.originalDialogStyle.width ?? '';
      dialogElement.style.height = this.originalDialogStyle.height ?? '';
      contentElement.style.maxHeight = this.originalDialogStyle.maxHeight ?? '';
    }
  }

  toggleMaximize1(): void {
    const overlayPane = document.querySelector('.cdk-overlay-pane.email-dialog-pane') as HTMLElement;
    if (!overlayPane) return;
    const dialogElement = overlayPane.querySelector('mat-dialog-container') as HTMLElement;
    const contentElement = dialogElement?.querySelector('.mat-dialog-content') as HTMLElement;
    if (!dialogElement || !contentElement) return;

    this.isMaximizedEmail = !this.isMaximizedEmail;
    if (this.isMaximizedEmail) {
      this.originalOverlayStyleEmail = {
        width: overlayPane.style.width,
        height: overlayPane.style.height,
        maxWidth: overlayPane.style.maxWidth,
        maxHeight: overlayPane.style.maxHeight
      };

      overlayPane.style.position = 'fixed';
      overlayPane.style.top = '0';
      overlayPane.style.left = '0';
      overlayPane.style.width = '100vw';
      overlayPane.style.height = '100vh';
      overlayPane.style.maxWidth = '100vw';
      overlayPane.style.maxHeight = '100vh';

      dialogElement.style.width = '100vw';
      dialogElement.style.maxWidth = '100vw';
      contentElement.style.maxHeight = '72vh';
    } else {
      overlayPane.style.position = this.originalOverlayStyle.position ?? '';
      overlayPane.style.top = this.originalOverlayStyle.top ?? '';
      overlayPane.style.left = this.originalOverlayStyle.left ?? '';
      overlayPane.style.margin = this.originalOverlayStyle.margin ?? '';
      overlayPane.style.width = this.originalOverlayStyle.width ?? '';
      overlayPane.style.height = this.originalOverlayStyle.height ?? '';
      overlayPane.style.maxWidth = this.originalOverlayStyle.maxWidth ?? '';
      overlayPane.style.maxHeight = this.originalOverlayStyle.maxHeight ?? '';

      dialogElement.style.width = this.originalDialogStyle.width ?? '';
      dialogElement.style.height = this.originalDialogStyle.height ?? '';
      contentElement.style.maxHeight = this.originalDialogStyle.maxHeight ?? '';
    }
  }

  openCancelAutoRenewDialog(data: any, templateRef: TemplateRef<any>) {
    this.selectedRecord = data;
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
    });
  }

  cancelAutoRenew() {
    if (!this.selectedRecord?.RzpSubscriptionId) {
      return;
    }
    this.api.cancelAutoRenew(this.selectedRecord?.RzpSubscriptionId).subscribe({
      next: (_res: any) => {
        this.matDialogRef.close();
        this.LoaderService.hide();
        this.toaster.success('Unsubscribed Successfully..');
        this.getDomain();
        this.loadData();
      },
      error: (_err: any) => {
        this.matDialogRef.close();
        this.LoaderService.hide();
        this.toaster.error('Something went wrong');
      },
    });
  }

  openPlanHistoryDialog(data: any, templateRef: TemplateRef<any>) {
    this.LoaderService.show();
    this.selectedRecord = data;
    this.activeTab = 0;
    this.getSubscriptionDetails();
    this.matDialogRef = this.dialog.open(templateRef, {
      disableClose: true,
      autoFocus: false,
    });
  }

  getSubscriptionDetails() {
    forkJoin({
      subscriptions: this.api.getSubscriptionDetails(this.selectedRecord?.Id),
      addOns: this.api.getAddOnDetails(this.selectedRecord?.Id),
      usageOverview: this.api.getUsageOverview(this.selectedRecord?.Id),
      aiSetting: this.api.getGeneralSetting(this.selectedRecord?.Id, this.aiKeyName)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ subscriptions, addOns, usageOverview, aiSetting }: any) => {
          if (usageOverview?.Success) {
            this.usageOverviewData = usageOverview?.Data;
          }

          if (subscriptions["Success"] && subscriptions["Data"]) {
            const subData = subscriptions["Data"] as PlanTransaction[];
            const addOnData = (addOns["Success"] && addOns["Data"]) ? addOns["Data"] as AddOnDetail[] : [];
            this.processSubscriptionData(subData, addOnData);
          }
          this.isAIEnabled = aiSetting?.Data?.Value?.toLowerCase() === 'true';
          this.LoaderService.hide();
        },
        error: (error: any) => {
          this.LoaderService.hide();
          this.toaster.error('Error fetching plan history details.', error);
        }
      });
  }

  private processSubscriptionData(allTransactions: PlanTransaction[], allAddOns: AddOnDetail[]): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.currentPlan = allTransactions.find(x =>
      x.TransactionTypeId === 2 && new Date(x.TransactionExpiryDate) >= today
    ) || null;

    if (this.currentPlan) {
      this.currentPlan.daysRemaining = this.calculateDaysRemaining(this.currentPlan.TransactionExpiryDate);
    }

    this.previousPlan = allTransactions.filter(x =>
      x.TransactionTypeId === 2 && (!this.currentPlan || x.Id !== this.currentPlan.Id)
    );

    this.mapWhatsAppToPlans(allTransactions);
    this.mapAddOnsToPlans(allAddOns);

    this.cd.detectChanges();
  }

  private mapWhatsAppToPlans(allTransactions: PlanTransaction[]): void {
    const whatsappTransactions = allTransactions.filter(x => x.TransactionTypeId === 1);

    if (whatsappTransactions.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (this.currentPlan) {
        this.currentPlan.whatsappStatus = this.getWhatsappStatusForPlan(whatsappTransactions, this.currentPlan, today, 'current');
      }

      this.previousPlan.forEach(plan => {
        plan.whatsappStatus = this.getWhatsappStatusForPlan(whatsappTransactions, plan, today, 'previous');
      });
    }
  }

  private getWhatsappStatusForPlan(whatsappTransactions: PlanTransaction[], plan: PlanTransaction, today: Date, type: 'current' | 'previous'): 'Active' | 'Expired' | null {
    const planStart = new Date(plan.Date);
    planStart.setHours(0, 0, 0, 0);
    const planEnd = new Date(plan.TransactionExpiryDate);
    planEnd.setHours(0, 0, 0, 0);

    // Find if any WhatsApp plan belongs to this period
    const matchingWhatsapp = whatsappTransactions.find(w => {
      const wExpiry = new Date(w.TransactionExpiryDate);
      wExpiry.setHours(0, 0, 0, 0);
      return type === 'current' ? wExpiry >= planStart : (wExpiry >= planStart && wExpiry <= planEnd);
    });

    if (matchingWhatsapp) {
      const wExpiry = new Date(matchingWhatsapp.TransactionExpiryDate);
      wExpiry.setHours(0, 0, 0, 0);
      if (type === 'current' && this.currentPlan) {
        this.currentPlan.whatsAppExpiryDate = wExpiry.toString();
      }
      const currentStatus = wExpiry >= today ? 'Active' : 'Expired';
      return type === 'current' ? currentStatus : 'Expired';
    }
    return null;
  }

  private mapAddOnsToPlans(allAddOns: AddOnDetail[]): void {
    if (this.currentPlan) {
      this.currentPlan.addOns = this.getAddOnsForPlan(allAddOns, this.currentPlan);
    }
    this.previousPlan.forEach(plan => {
      plan.addOns = this.getAddOnsForPlan(allAddOns, plan);
    });
  }

  private getAddOnsForPlan(allAddOns: AddOnDetail[], plan: PlanTransaction): AddOnDetail[] {
    if (!allAddOns) return [];

    const planUsage = Array.isArray(this.usageOverviewData)
      ? this.usageOverviewData.find(u => u.TransactionId === plan.Id)
      : null;

    const planStart = new Date(plan.Date);
    planStart.setHours(0, 0, 0, 0);
    const planEnd = new Date(plan.TransactionExpiryDate);
    planEnd.setHours(0, 0, 0, 0);

    const filtered = allAddOns
      .filter(addon => {
        const comparisonDate = new Date(addon.ExpiryDate ?? addon.AddOnDate);
        comparisonDate.setHours(0, 0, 0, 0);
        return comparisonDate >= planStart && comparisonDate <= planEnd;
      })
      .map(addon => ({
        ...addon,
        TypeName: addon.AddOnTypes?.TypeName ?? 'Other'
      }));


    // Aggregate by TypeName
    const aggregated = filtered.reduce((acc: { [key: string]: AddOnDetail }, curr) => {
      const typeName = curr.TypeName || 'Other';
      if (!acc[typeName]) {
        acc[typeName] = { ...curr };
      } else {
        acc[typeName].AddOnCount += curr.AddOnCount;
      }
      return acc;
    }, {});

    return Object.values(aggregated).map(addon => {
      let usedCount = 0;
      if (planUsage) {
        if (addon.TypeName === 'User') usedCount = planUsage.TotalAddonUsers;
        else if (addon.TypeName === 'Location') usedCount = planUsage.TotalLocations;
        else if (addon.TypeName === 'Patients') usedCount = planUsage.TotalPatients;
        else if (addon.TypeName === 'Storage') usedCount = planUsage.TotalStorage;
      }
      return {
        ...addon,
        addonUsage: usedCount
      };
    });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  calculateDaysRemaining(expiryDate: string): number {
    if (!expiryDate) return 0;

    const expiry = new Date(expiryDate);
    const today = new Date();

    // Create date-only objects using year, month, and day components
    const expiryDateOnly = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Calculate difference in days
    const diffDays = Math.floor((expiryDateOnly.valueOf() - todayDateOnly.valueOf()) / (1000 * 60 * 60 * 24));

    return Math.max(diffDays, 0);
  }

  onCurrencyChange(event: any): void {
    this.selectedCurrencyId = event.value;
    if (this.plans && this.plans.length > 0) {
      const currencyCode = this.getCurrencyCodeById(this.selectedCurrencyId);
      this.updatePlanPrices(currencyCode);
    }
  }

  /** A client's TransactionCurrencyId is whichever per-country Currency row they signed up with
   *  (e.g. Ecuador's USD row), but the picker lists one row per code — map it to that code's
   *  picker entry so the dropdown preselects correctly; unknown/unsupported codes fall back to INR. */
  toPickerCurrencyId(currencyId: number): number {
    const code = (this.currencyList ?? []).find((c: any) => c.Id === currencyId)?.CurrencyCode
      ?? this.currencyMap.find((c: any) => c.Id === currencyId)?.CurrencyCode;
    const entry = this.currencyMap.find((c: any) => c.CurrencyCode === code)
      ?? this.currencyMap.find((c: any) => c.CurrencyCode === 'INR');
    return entry ? entry.Id : currencyId;
  }

  getCurrencyCodeById(currencyId: number): string {
    const currency = this.currencyMap.find((c: any) => c.Id === currencyId);
    return currency ? currency.CurrencyCode : 'INR';
  }

  getCurrencySymbolById(currencyId: number): string {
    const currency = this.currencyMap.find((c: any) => c.Id === currencyId);
    return currency ? currency.CurrencySymbol : '₹';
  }

  copyToClipboard(text: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        this.toaster.success('Copied to clipboard');
      }).catch(err => {
        console.error('Could not copy text: ', err);
      });
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.toaster.success('Copied to clipboard');
    }
  }

  getPlanPrice(planId: number, isYearly: boolean): string {
    const currencyCode = this.getCurrencyCodeById(this.selectedCurrencyId);
    const planPrice = this.planPrices[planId];

    if (!planPrice) return '0';

    const priceType = isYearly ? 'annual' : 'monthly';
    return planPrice[currencyCode]?.[priceType] ?? '0';
  }

  updatePlanPrices(currencyCode: string): void {
    this.plans = this.plans.map(plan => ({
      ...plan,
      monthlyPrice: this.planPrices[plan.id]?.[currencyCode]?.monthly ?? plan.monthlyPrice,
      annualPrice: this.planPrices[plan.id]?.[currencyCode]?.annual ?? plan.annualPrice
    }));
  }

  getCurrency(): void {
    this.api.getCountry().subscribe({
      next: (res: any) => {
        if (!res?.Success || !Array.isArray(res.Data)) return;

        // Common/GetCountry returns one Currency row per COUNTRY, so several countries share a code
        // (USD: United States, Ecuador, El Salvador, Puerto Rico, ...) — keep a single entry per
        // code, the lowest Id (the original/primary row, e.g. INR = 1, USD = United States), so
        // the plan-currency picker lists each currency once.
        const byCode = new Map<string, any>();
        res.Data
          .filter((c: any) => c.CurrencyCode === 'INR' || c.CurrencyCode === 'USD')
          .sort((a: any, b: any) => a.Id - b.Id)
          .forEach((c: any) => { if (!byCode.has(c.CurrencyCode)) byCode.set(c.CurrencyCode, c); });
        this.currencyMap = [...byCode.values()];

        if (!this.currencyMap.length) return;

        const defaultcurrency = this.currencyMap.find((c: any) => c.Id === 1) ?? this.currencyMap[0];
        this.selectedCurrencyId = defaultcurrency.Id;
      },
      error: (err: any) => console.error(err)
    });
  }

  get ruleControls(): FormArray {
    return this.creditRuleForm.get('rules') as FormArray;
  }
  creditRuleInit() {
    this.creditRuleForm = this.FormBuilder.group({
      rules: this.FormBuilder.array([])
    });
  }
  private buildRules(data: any[]) {
    const arr = this.FormBuilder.array(
      (data || []).map(x =>
        this.FormBuilder.group({
          Id: [x.Id],
          Name: [x.Name],
          CreditValue: [x.CreditValue, [Validators.required, Validators.min(0)]]
        })
      )
    );
    this.creditRuleForm.setControl('rules', arr);
  }
  async getCreditRules() {
    this.loading = true;
    this.LoaderService.show();
    const response: any = await firstValueFrom(this.api.getCreditRule(this.selectedUser.Id));
    if (response.Success) {
      this.buildRules(response?.Data);
      this.loading = false;
      this.LoaderService.hide();
    }
    else {
      this.loading = false;
      this.LoaderService.hide();
    }
  }
  async openCreditRuleDialog(dataItem: any) {
    this.selectedUser = dataItem;
    this.creditRuleInit();
    await this.getCreditRules();
    this.dialog.open(this.creditRuleDialog, {
      disableClose: true,
      panelClass: 'popup_custom_width_small',
      autoFocus: false,
      width: '500px'
    });
  }
  UpdateMality() {
    if (this.creditRuleForm.invalid) return;
    this.loading = true;

    var payload = {
      CompanyId: this.selectedUser.Id,
      modalityList: this.creditRuleForm.value.rules
    }
    console.log(payload);

    this.api.updateCreditRules(payload).subscribe((res: any) => {
      if (res.Success) {
        this.toaster.success('Credit Rules updated successfully');
      } else {
        this.toaster.error('Failed to update Credit Rules');
      }
      this.loading = false;
      this.close();
    });

    setTimeout(() => {
      this.loading = false;
      this.close();
    }, 1000);
  }

  CreateDomain(companyId: any) {
    this.api.createDomain(companyId).subscribe({
      next: () => {
        this.getDomain();
        this.loadData();
      },
      error: () => {
        this.getDomain();
        this.loadData();
      }
    });
  }

}